import { LOCALES, LOCALE_NAMES, LOCALE_TAGS } from '../../src/i18n/index';
import {
  T, TN, addDaysISO, api, chooseLocale, dateIn, errorText, esc, fmtDate, fmtTime, getLocale, hhmm, minutesToLabel, setLocale,
  todayIn, type ApiError,
} from '../shared/client';
import { openConfigScreen } from './config';
import { openExtrasScreen } from './extras';
import { openGuestsScreen } from './guests';
import { openReportsScreen } from './reports';
import { tableBox } from '../../src/domain/layout';

type Mode =
  | null
  | { kind: 'seat'; partyId: string }
  | { kind: 'assign'; partyId: string }
  | { kind: 'move'; partyId: string }
  | { kind: 'waitseat'; waitId: string }
  | { kind: 'walkin'; partySize: number; guestName: string };

type View = 'floor' | 'list' | 'timeline' | 'overview' | 'calendar';

const S = {
  rid: '',
  tz: 'America/New_York',
  date: '',
  shiftId: '' as string,
  /** Floor plan shown on the floor view when the restaurant has more than one. */
  planId: '' as string,
  view: 'floor' as View,
  data: null as any,
  sel: null as null | { kind: 'party' | 'table' | 'wait'; id: string },
  mode: null as Mode,
  collapsed: new Set<string>(['finished', 'removed']),
  busy: false,
  modalOpen: false,
  user: null as null | { id: string; name: string; email: string; role: string; restaurantId: string; permissions: string[] },
  pollTimer: 0 as number,
  search: '' as string,
  confirmedTable: false,
  /** Minutes since midnight: show the floor as it will be at that time. Null = now. */
  asOf: null as number | null,
  cal: null as null | { month: string; days: any[] },
  calMonth: '',
  feedUnseen: 0,
  menuOpen: false,
  switchTo: [] as { restaurantId: string; name: string }[],
};

/** Cosmetic only: the server enforces every permission. */
const can = (perm: string) => !!S.user?.permissions.includes(perm);

const app = document.getElementById('app')!;

let siteRid: string | null | undefined;
async function loginRestaurantId(): Promise<string | null> {
  const fromUrl = new URLSearchParams(location.search).get('rid');
  if (fromUrl && /^[0-9a-f-]{36}$/i.test(fromUrl)) return fromUrl;
  if (siteRid === undefined) {
    try {
      siteRid = (await api<{ restaurantId: string | null }>('GET', '/v1/site')).restaurantId;
    } catch {
      siteRid = null;
    }
  }
  return siteRid;
}
const modalRoot = document.getElementById('modal-root')!;
const toastEl = document.getElementById('toast')!;
let toastTimer: number | undefined;

// ------------------------------------------------------------------ helpers

function toast(msg: string, isErr = false) {
  toastEl.textContent = msg;
  toastEl.className = `show${isErr ? ' err' : ''}`;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toastEl.className = ''), isErr ? 5000 : 2200);
}

function errMsg(e: unknown): string {
  const err = e as ApiError;
  const alts = err.details?.alternatives as { localTime: string }[] | undefined;
  const code = err.details?.reason ? String(err.details.reason) : '';
  const known = code && T(`reason.${code}`) !== `reason.${code}`;
  const reason = code ? ` (${known ? T(`reason.${code}`) : code.replace(/_/g, ' ')})` : '';
  const near = alts?.length ? `. ${T('h.nearby', { times: alts.map((a) => a.localTime).join(', ') })}` : '';
  return `${errorText(e)}${reason}${near}`;
}

async function act<T>(fn: () => Promise<T>, ok?: string): Promise<T | undefined> {
  try {
    const r = await fn();
    if (ok) toast(ok);
    await load();
    return r;
  } catch (e) {
    toast(errMsg(e), true);
    return undefined;
  }
}

/** Held in memory only, and only while a dialog that needs it is open. */
let sessionPasscode: string | null = null;

function askPasscode(message = ''): Promise<string | null> {
  return new Promise((resolve) => {
    S.modalOpen = true;
    modalRoot.innerHTML = `<div class="scrim"><form class="modal" id="pc-form" role="dialog" aria-modal="true" aria-labelledby="pc-title" style="max-width:380px">
      <h2 id="pc-title">${esc(T('h.pc.title'))}</h2>
      <p class="muted small">${esc(T('h.pc.body'))}</p>
      ${message ? `<div class="warn-box" role="alert">${esc(message)}</div>` : ''}
      <input id="pc-input" type="password" inputmode="numeric" autocomplete="off" maxlength="8" aria-label="${esc(T('h.pc.aria'))}" required>
      <div class="row-end"><button type="button" id="pc-cancel">${esc(T('common.cancel'))}</button><button class="primary" type="submit">${esc(T('h.continue'))}</button></div>
    </form></div>`;
    const input = document.getElementById('pc-input') as HTMLInputElement;
    input.focus();
    const done = (v: string | null) => {
      closeModal();
      resolve(v);
    };
    document.getElementById('pc-cancel')!.addEventListener('click', () => done(null));
    document.getElementById('pc-form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      done(input.value.trim() || null);
    });
  });
}

/** Run an action that may need the passcode, prompting once and retrying. */
async function protectedAction(fn: (passcode?: string) => Promise<unknown>, ok: string, passcode?: string): Promise<void> {
  try {
    await fn(passcode);
    toast(ok);
    await load();
  } catch (e) {
    const err = e as ApiError;
    if (err.code === 'passcode_required' || err.code === 'passcode_invalid') {
      const code = await askPasscode(err.code === 'passcode_invalid' ? T('h.pc.wrong') : '');
      if (code) return protectedAction(fn, ok, code);
      return;
    }
    toast(errMsg(e), true);
  }
}

const D = () => S.data;
const party = (id: string) =>
  [...D().parties.reservations, ...D().parties.seated, ...D().parties.finished, ...D().parties.removed].find((p: any) => p.id === id);
const table = (id: string) => D().tables.find((t: any) => t.id === id);
const statusInfo = (code: string) => D().statuses.find((s: any) => s.code === code);
/** A status name in the current language (the server label is English and only a fallback). */
function statusLabel(code: string): string {
  const k = `status.${code}`;
  const v = T(k);
  return v === k ? (statusInfo(code)?.label ?? code) : v;
}
/** Built-in role names are translated; custom roles show as the restaurant named them. */
const roleLabel = (name: string) => {
  const k = `role.${name}`;
  const v = T(k);
  return v === k ? name : v;
};
const permLabel = (code: string, fallback: string) => {
  const k = `perm.${code}`;
  const v = T(k);
  return v === k ? fallback : v;
};
const sourceLabel = (src: string) => {
  const k = `h.source.${src}`;
  const v = T(k);
  return v === k ? src.replace('_', ' ') : v;
};
const occasionLabel = (o: string) => {
  const k = `occasion.${o}`;
  const v = T(k);
  return v === k ? o : v;
};
const tm = (iso: string) => fmtTime(iso, S.tz);

function localMinutes(iso: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: S.tz }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === 'hour')!.value);
  const m = Number(parts.find((p) => p.type === 'minute')!.value);
  return h * 60 + m;
}

/** Minutes since local midnight of the displayed date; negative or >1440 for other days. */
function minutesOnShownDate(iso: string): number {
  const dayOf = dateIn(iso, S.tz);
  const a = Date.UTC(...(dayOf.split('-').map(Number) as [number, number, number]).map((v, i) => (i === 1 ? v - 1 : v)) as [number, number, number]);
  const b = Date.UTC(...(S.data.date.split('-').map(Number) as [number, number, number]).map((v, i) => (i === 1 ? v - 1 : v)) as [number, number, number]);
  return Math.round((a - b) / 86_400_000) * 1440 + localMinutes(iso);
}

// ------------------------------------------------------------------ data

let loadSeq = 0;
/**
 * Fetch and draw the shift. Responses can arrive out of order (the 15 s refresh
 * versus a click), so only the newest request may update the screen.
 */
async function load() {
  const seq = ++loadSeq;
  const q = new URLSearchParams({ date: S.date });
  if (S.shiftId) q.set('shiftId', S.shiftId);
  const data = await api<any>('GET', `/v1/restaurants/${S.rid}/host/state?${q}`);
  if (seq !== loadSeq) return;
  S.data = data;
  S.tz = S.data.restaurant.timezone;
  if (S.data.shiftId) S.shiftId = S.data.shiftId;
  if (S.sel?.kind === 'party' && !party(S.sel.id)) S.sel = null;
  render();
  void refreshFeedBadge();
}

const feedKey = () => `feed-seen:${S.rid}`;
async function refreshFeedBadge() {
  try {
    let since = '';
    try {
      since = localStorage.getItem(feedKey()) ?? '';
    } catch {
      /* private mode: show everything as new */
    }
    const q = since ? `?since=${encodeURIComponent(since)}&limit=100` : '?limit=100';
    const r = await api<{ items: any[] }>('GET', `/v1/restaurants/${S.rid}/feed${q}`);
    // Only things guests or other staff did; your own actions are not news.
    const mine = S.user?.email ?? '';
    const n = r.items.filter((i) => i.actor !== mine).length;
    if (n !== S.feedUnseen) {
      S.feedUnseen = n;
      const b = document.querySelector('[data-act="feed"]');
      if (b) b.innerHTML = `${esc(T('h.y.feed'))}${n ? ` <span class="pill live">${n}</span>` : ''}`;
    }
  } catch {
    /* the feed is a convenience */
  }
}

async function loadCalendar() {
  const month = S.calMonth || S.date.slice(0, 7);
  S.calMonth = month;
  S.cal = await api(`GET`, `/v1/restaurants/${S.rid}/calendar?month=${month}`);
  render();
}

async function loadSwitchOptions() {
  try {
    S.switchTo = (await api<any>('GET', '/v1/auth/switch-options')).restaurants;
  } catch {
    S.switchTo = [];
  }
}

/** A small language selector, used on the sign-in screen and in the header. */
function languagePicker(): string {
  const offered = (S.data?.restaurant?.locales as string[] | undefined) ?? [...LOCALES];
  const langs = LOCALES.filter((l) => offered.includes(l));
  if (langs.length < 2) return '';
  return `<label class="langpick"><span class="sr-only">${esc(T('lang.label'))}</span>
    <select data-lang-select aria-label="${esc(T('lang.label'))}">${langs
      .map((l) => `<option value="${l}" ${l === getLocale() ? 'selected' : ''}>${esc(LOCALE_NAMES[l])}</option>`)
      .join('')}</select></label>`;
}

/** Held only between the password step and the code step of one sign-in. */
let pendingLogin: { email: string; password: string } | null = null;
let ssoOptions: { restaurantId: string | null; sso: { enforce: boolean } | null } = { restaurantId: null, sso: null };

function loginView(message = '', mode: 'password' | 'code' | 'forgot' | 'reset' = 'password', info = '') {
  window.clearInterval(S.pollTimer);
  S.user = null;
  S.data = null;
  S.sel = null;
  closeModal();
  document.title = T('h.login.doc');
  const box = (inner: string, title = T('h.title'), sub = T('h.login.sub')) => `
  <main class="login">
    <div class="login-card">
      ${languagePicker()}
      <h1>${esc(title)}</h1>
      <p class="muted">${esc(sub)}</p>
      ${message ? `<div class="warn-box" role="alert">${esc(message)}</div>` : ''}
      ${info ? `<div class="info-box" role="status">${esc(info)}</div>` : ''}
      ${inner}
    </div>
  </main>`;
  if (mode === 'code') {
    app.innerHTML = box(
      `<form id="login-form" aria-label="${esc(T('h.login.twostep'))}">
        <label for="li-code">${esc(T('h.login.code_label'))}</label>
        <input id="li-code" name="code" inputmode="numeric" autocomplete="one-time-code" maxlength="20" required autofocus>
        <p class="muted small">${esc(T('h.login.code_hint'))}</p>
        <p style="margin:16px 0 0"><button class="primary" type="submit" style="width:100%">${esc(T('h.login.verify'))}</button></p>
        <p style="margin:10px 0 0"><button class="link" type="button" data-act="back-login">${esc(T('h.login.other_account'))}</button></p>
      </form>`,
      T('h.login.twostep'),
      T('h.login.one_more'),
    );
  } else if (mode === 'forgot') {
    app.innerHTML = box(
      `<form id="forgot-form">
        <label for="fg-email">${esc(T('h.login.email'))}</label>
        <input id="fg-email" name="email" type="email" autocomplete="username" required autofocus>
        <p style="margin:16px 0 0"><button class="primary" type="submit" style="width:100%">${esc(T('h.login.reset_link'))}</button></p>
        <p style="margin:10px 0 0"><button class="link" type="button" data-act="back-login">${esc(T('h.login.back'))}</button></p>
      </form>`,
      T('h.login.reset_title'),
      T('h.login.reset_sub'),
    );
  } else if (mode === 'reset') {
    app.innerHTML = box(
      `<form id="reset-form">
        <label for="rs-pass">${esc(T('h.login.new_pass'))}</label>
        <input id="rs-pass" name="password" type="password" autocomplete="new-password" minlength="10" required autofocus>
        <p style="margin:16px 0 0"><button class="primary" type="submit" style="width:100%">${esc(T('h.login.set_pass'))}</button></p>
      </form>`,
      T('h.login.choose_title'),
      T('h.login.once'),
    );
  } else {
    const sso = ssoOptions.sso && ssoOptions.restaurantId
      ? `<p style="margin:0 0 14px"><a class="btn-link" href="/v1/auth/sso/start?restaurantId=${esc(ssoOptions.restaurantId)}">${esc(T('h.login.sso'))}</a></p>
         <p class="muted small" style="margin:0 0 10px">${esc(ssoOptions.sso.enforce ? T('h.login.sso_enforced') : T('h.login.or_password'))}</p>`
      : '';
    app.innerHTML = box(
      `${sso}<form id="login-form" aria-label="${esc(T('h.login.submit'))}">
        <label for="li-email">${esc(T('h.login.email'))}</label>
        <input id="li-email" name="email" type="email" autocomplete="username" required ${sso ? '' : 'autofocus'}>
        <label for="li-pass" style="margin-top:10px">${esc(T('h.login.password'))}</label>
        <input id="li-pass" name="password" type="password" autocomplete="current-password" required>
        <p style="margin:16px 0 0"><button class="primary" type="submit" style="width:100%">${esc(T('h.login.submit'))}</button></p>
        <p style="margin:10px 0 0"><button class="link" type="button" data-act="forgot">${esc(T('h.login.forgot'))}</button></p>
      </form>`,
    );
  }
}

async function startSession() {
  const p = new URLSearchParams(location.search);
  S.rid = S.user!.restaurantId; // a staff account belongs to exactly one restaurant
  const meta = await api<any>('GET', `/v1/restaurants/${S.rid}`);
  setLocale(chooseLocale(meta.locales, meta.defaultLocale), false);
  S.tz = meta.timezone;
  S.date = p.get('date') ?? todayIn(S.tz);
  S.shiftId = '';
  await loadSwitchOptions();
  await load();
  window.clearInterval(S.pollTimer);
  S.pollTimer = window.setInterval(() => {
    if (!S.modalOpen && !S.busy && S.user) void load().catch(() => undefined);
  }, 15000);
}

async function init() {
  // Before we know the restaurant: the person's earlier choice, else their browser.
  setLocale(chooseLocale(null, null), false);
  const p = new URLSearchParams(location.search);
  const resetToken = p.get('reset');
  const ssoError = p.get('sso_error');
  // Keep one-time values out of the address bar and history.
  if (resetToken || ssoError) history.replaceState(null, '', location.pathname);
  if (resetToken) {
    pendingResetToken = resetToken;
    loginView('', 'reset');
    return;
  }
  try {
    const me = await api<any>('GET', '/v1/auth/me');
    S.user = me.user;
  } catch {
    try {
      ssoOptions = await api('GET', '/v1/auth/options');
    } catch {
      /* password sign-in still works */
    }
    loginView(ssoError ?? '');
    return;
  }
  await startSession();
}

let pendingResetToken = '';

window.addEventListener('auth:expired', () => {
  if (S.user) loginView(T('h.login.session_ended'));
});

async function finishLogin(user: any) {
  pendingLogin = null;
  S.user = user;
  try {
    await startSession();
  } catch (e) {
    const err = e as ApiError;
    // The restaurant requires two-step sign-in and this person has not enrolled yet.
    if (err.code === 'mfa_setup_required') {
      app.innerHTML = `<main class="login"><div class="login-card"><h1>${esc(T('h.login.mfa_title'))}</h1><p class="muted">${esc(T('h.login.mfa_body'))}</p></div></main>`;
      await securityModal(true);
      return;
    }
    loginView(errMsg(e));
  }
}

document.addEventListener('submit', async (ev) => {
  const form = ev.target as HTMLFormElement;
  if (!['login-form', 'forgot-form', 'reset-form'].includes(form.id)) return;
  ev.preventDefault();
  const fd = new FormData(form);
  if (form.id === 'forgot-form') {
    try {
      await api('POST', '/v1/auth/password-reset/request', { email: String(fd.get('email') ?? '').trim() });
    } catch {
      /* the answer is the same either way */
    }
    loginView('', 'password', T('h.login.reset_sent'));
    return;
  }
  if (form.id === 'reset-form') {
    try {
      await api('POST', '/v1/auth/password-reset/confirm', { token: pendingResetToken, password: String(fd.get('password') ?? '') });
      pendingResetToken = '';
      loginView('', 'password', T('h.login.pass_changed'));
    } catch (e) {
      loginView(errMsg(e), 'reset');
    }
    return;
  }
  // sign in (password step or code step)
  const email = pendingLogin?.email ?? String(fd.get('email') ?? '').trim();
  const password = pendingLogin?.password ?? String(fd.get('password') ?? '');
  const code = fd.get('code') ? String(fd.get('code')).trim() : undefined;
  try {
    // On a customer's own domain, or with ?rid=, the sign-in belongs to that restaurant.
    const restaurantId = (await loginRestaurantId()) ?? undefined;
    const r = await api<any>('POST', '/v1/auth/login', { email, password, code, restaurantId });
    await finishLogin(r.user);
  } catch (e) {
    const err = e as ApiError;
    if (err.code === 'mfa_required') {
      pendingLogin = { email, password };
      loginView('', 'code');
      return;
    }
    if (code) {
      loginView(err.status === 429 || err.status === 423 ? errorText(e) : T('h.login.bad_code'), 'code');
      return;
    }
    pendingLogin = null;
    loginView(
      err.status === 429 || err.status === 423 || err.status === 403 ? errorText(e) : T('h.login.bad_creds'),
    );
  }
});

// ------------------------------------------------------------------ views

/** Text colour that stays readable on a status colour (white or near-black, whichever contrasts more). */
function inkOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex));
  if (!m) return '#fff';
  const c = [0, 2, 4].map((i) => {
    const v = parseInt(m[1].slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return 1.05 / (L + 0.05) >= (L + 0.05) / 0.05 ? '#fff' : '#0b1220';
}

function header(): string {
  const d = D();
  const live = d.date === todayIn(S.tz);
  return `
  <header class="top">
    <button class="ghost menu-btn" data-act="menu" aria-label="${esc(T('n.menu'))}" aria-expanded="${S.menuOpen}">☰</button>
    <span class="brand">${esc(d.restaurant.name)}</span>
    <span class="seg" role="group" aria-label="${esc(T('h.date_aria'))}">
      <button data-act="date" data-n="-1" aria-label="${esc(T('h.prev_day'))}">‹</button>
      <button data-act="date" data-n="0">${live ? esc(T('h.today')) : esc(fmtDate(d.date))}</button>
      <button data-act="date" data-n="1" aria-label="${esc(T('h.next_day'))}">›</button>
    </span>
    <span class="muted">${esc(fmtDate(d.date))}</span>
    <span class="seg" role="tablist" aria-label="${esc(T('h.shift_aria'))}">
      ${d.shifts.map((s: any) => `<button role="tab" data-act="shift" data-id="${s.id}" aria-selected="${s.id === d.shiftId}">${esc(s.name)}</button>`).join('')}
    </span>
    ${d.closed ? `<span class="pill" style="border-color:var(--danger);color:var(--danger)">${esc(T('h.closed'))}${d.specialDay ? ` · ${esc(d.specialDay)}` : ''}</span>` : ''}
    <span class="pill ${live ? 'live' : ''}">${esc(T('h.covers', { n: d.totals.covers }))}</span>
    <span class="spacer"></span>
    <span class="seg" role="group" aria-label="${esc(T('h.view_aria'))}">
      ${(['floor', 'list', 'timeline', 'calendar', 'overview'] as View[]).map((v) => `<button data-act="view" data-v="${v}" aria-pressed="${S.view === v}">${esc(T(`h.view.${v}`))}</button>`).join('')}
    </span>
    ${can('reservations.manage') ? `<button class="primary" data-act="book">${esc(T('h.book'))}</button>` : ''}
    ${can('waitlist.manage') ? `<button data-act="waitlist-add">${esc(T('h.waitlist_add'))}</button>` : ''}
    ${can('settings.manage') ? `<button data-act="statuses" title="${esc(T('h.statuses_title'))}">${esc(T('h.statuses'))}</button>` : ''}
    <button data-act="feed" title="${esc(T('h.y.feed_title'))}">${esc(T('h.y.feed'))}${S.feedUnseen ? ` <span class="pill live">${S.feedUnseen}</span>` : ''}</button>
    <button data-act="problems" title="${esc(T('h.y.problems_title'))}">${esc(T('h.y.problems'))}${d.problems?.length ? ` <span class="pill" style="border-color:var(--warn);color:var(--warn)">${d.problems.length}</span>` : ''}</button>
    <span class="user" title="${esc(S.user?.email ?? '')}">${esc(S.user?.name ?? '')} <span class="muted small">· ${esc(roleLabel(S.user?.role ?? ''))}</span></span>
    ${languagePicker()}
  </header>${sideMenu()}`;
}

/** The slide-in menu: every part of the app, grouped like the reference design. */
function sideMenu(): string {
  if (!S.menuOpen) return '';
  const d = D();
  const item = (go: string, icon: string, label: string, show = true) =>
    show ? `<button class="nav-item" data-act="nav" data-go="${go}"><span class="nav-ico" aria-hidden="true">${icon}</span>${esc(label)}</button>` : '';
  const cfg = can('config.edit');
  return `<div class="side-scrim" data-act="menu-close"></div>
  <aside class="side" aria-label="${esc(T('n.menu'))}">
    <div class="side-top"><button class="ghost" data-act="menu-close" aria-label="${esc(T('n.close'))}">✕</button><span class="spacer"></span>
      ${S.switchTo.length ? `<label class="side-switch"><span>${esc(T('n.change'))}</span><select data-switch aria-label="${esc(T('h.y.switch'))}"><option value="">${esc(d.restaurant.name)}</option>${S.switchTo
        .map((r) => `<option value="${r.restaurantId}">${esc(r.name)}</option>`)
        .join('')}</select></label>` : ''}</div>
    <div class="side-id"><div class="side-logo" aria-hidden="true">${esc(String(d.restaurant.name).slice(0, 1).toUpperCase())}</div>
      <div><strong>${esc(d.restaurant.name)}</strong><div class="small muted">${esc(S.user?.name ?? '')} · ${esc(roleLabel(S.user?.role ?? ''))}</div></div></div>
    <nav class="side-nav">
      ${item('front', '▦', T('n.front'))}
      ${item('planning', '📅', T('n.planning'), cfg)}
      ${item('floorplan', '▤', T('n.floorplan'), cfg)}
      ${item('private', '🥂', T('n.private'))}
      ${item('reporting', '📈', T('n.reporting'), can('reports.view'))}
      ${item('menus', '📖', T('n.menus'))}
      ${item('marketing', '📣', T('n.marketing'), can('marketing.manage') && !!d.restaurant.marketingEnabled)}
      ${item('alerts', '🔔', T('n.alerts'))}
      ${item('print', '🖨', T('n.print'))}
      ${item('guests', '👥', T('n.guests'), can('guests.manage'))}
      ${item('notes', '📝', T('n.notes'))}
      <div class="side-head">${esc(T('n.admin'))}</div>
      ${item('profile', '🏠', T('n.profile'), cfg)}
      ${item('settings', '⚙', T('n.settings'), cfg)}
      ${item('policies', '📋', T('n.policies'), cfg)}
      ${item('widget', '🖥', T('n.widget'), cfg)}
      ${item('integrations', '🔌', T('n.integrations'), can('integrations.manage'))}
      ${item('accounts', '👤', T('n.accounts'), can('staff.manage'))}
      ${item('billing', '🧾', T('n.billing'), can('billing.view'))}
      ${item('security', '🔒', T('n.security'))}
      ${item('log', '🕘', T('n.log'), can('staff.manage'))}
      ${item('signout', '⎋', T('n.signout'))}
    </nav>
    <div class="side-foot small muted">${esc(T('n.id', { id: String(S.rid).slice(0, 8) }))}</div>
  </aside>`;
}

function partyRow(p: any): string {
  const col = statusInfo(p.status)?.color ?? '#8b95a5';
  const sel = S.sel?.kind === 'party' && S.sel.id === p.id;
  const timeCls = p.late ? 'late' : '';
  const sub =
    p.group === 'seated' && p.elapsedMinutes != null
      ? `<span class="${p.overdue ? 'over' : ''}">${minutesToLabel(p.elapsedMinutes)}</span> · ${esc(statusLabel(p.status))}`
      : `${esc(statusLabel(p.status))}${p.tags.length ? ' · ' + esc(p.tags.join(', ')) : ''}${p.occasion ? ' · ' + esc(occasionLabel(p.occasion)) : ''}`;
  return `
  <div class="party" role="button" tabindex="0" data-act="sel-party" data-id="${p.id}" aria-pressed="${sel}">
    <span class="bar" style="background:${col}"></span>
    <span class="t ${timeCls}">${tm(p.startsAt).replace(' ', '&nbsp;')}<br><span class="s">${esc(T('common.ppl', { n: p.partySize }))}</span></span>
    <span><span class="n">${esc(p.name)}</span><br><span class="s">${sub}</span></span>
    <span class="tbl" title="${p.tableLabels.length ? esc(T('h.table_n', { label: p.tableLabels.join('+') })) : esc(T('h.unassigned'))}">${p.tableLabels.length ? esc(p.tableLabels.join('+')) : '—'}</span>
  </div>`;
}

function waitRow(w: any): string {
  const sel = S.sel?.kind === 'wait' && S.sel.id === w.id;
  return `
  <div class="party" role="button" tabindex="0" data-act="sel-wait" data-id="${w.id}" aria-pressed="${sel}">
    <span class="bar" style="background:${w.status === 'notified' ? 'var(--ok)' : 'var(--warn)'}"></span>
    <span class="t">${w.partySize}<br><span class="s">${esc(T('common.ppl', { n: '' }).trim())}</span></span>
    <span><span class="n">${esc(w.name)}</span><br><span class="s ${w.overQuote ? 'over' : ''}">${esc(T('h.wait.progress', { waited: w.waitedMinutes, quoted: w.quotedMinutes }))}${w.status === 'notified' ? ' · ' + esc(T('h.wait.notified')) : ''}</span></span>
    <span class="tbl">⏱</span>
  </div>`;
}

function group(key: string, title: string, items: string, totals: { parties: number; covers: number }): string {
  const open = !S.collapsed.has(key);
  return `
  <section class="group">
    <h3 data-act="toggle" data-id="${key}" role="button" tabindex="0" aria-expanded="${open}">
      <span>${esc(title)}</span><span>${totals.parties} · ${esc(T('common.ppl', { n: totals.covers }))} ${open ? '▾' : '▸'}</span>
    </h3>
    ${open ? items || `<div class="party"><span></span><span class="muted small" style="grid-column:2/-1">${esc(T('h.none'))}</span></div>` : ''}
  </section>`;
}

function listsPanel(): string {
  const d = D();
  const t = d.totals;
  return `
  <aside class="lists" aria-label="${esc(T('h.parties_aria'))}">
    <div class="finder">
      <input id="host-search" type="search" autocomplete="off" placeholder="${esc(T('h.x.search_ph'))}" aria-label="${esc(T('h.x.search_ph'))}" value="${esc(S.search)}">
      <div id="search-results" class="search-results" role="listbox" aria-label="${esc(T('h.x.search_results'))}"></div>
    </div>
    ${
      can('waitlist.manage') && (d.waitQuotes ?? []).length
        ? `<div class="quotes" role="group" aria-label="${esc(T('h.x.quotes'))}">${(d.waitQuotes as any[])
            .map(
              (q) => `<button data-act="quote" data-n="${q.partySize}" data-min="${q.minutes}" title="${esc(T('h.x.quote_title', { n: q.partySize, m: q.minutes }))}">
                <strong>${q.freeNow ? '✓' : `${q.minutes}m`}</strong><small>${q.partySize}</small></button>`,
            )
            .join('')}<button data-act="waitlist-add" aria-label="${esc(T('h.waitlist_add'))}">+</button></div>`
        : ''
    }
    ${group('waitlist', T('h.group.waitlist'), d.parties.waitlist.map(waitRow).join(''), t.waitlist)}
    ${group('reservations', T('h.group.reservations'), d.parties.reservations.map(partyRow).join(''), t.reservations)}
    ${group('seated', T('h.group.seated'), d.parties.seated.map(partyRow).join(''), t.seated)}
    ${group('finished', T('h.group.finished'), d.parties.finished.map(partyRow).join(''), t.finished)}
    ${group('removed', T('h.group.removed'), d.parties.removed.map(partyRow).join(''), t.removed)}
  </aside>`;
}

/** How a table will look at a given time of the shown day, from its bookings. */
function tableAsOf(t: any, at: number): any {
  const hit = (t.bookings as any[]).find((b) => minutesOnShownDate(b.startsAt) <= at && at < minutesOnShownDate(b.endsAt));
  const next = (t.bookings as any[])
    .filter((b) => minutesOnShownDate(b.startsAt) > at)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
  const base = { ...t, nextStartsAt: next?.startsAt ?? null, nextPartyName: next?.partyId ? party(next.partyId)?.name : null, nextPartySize: next?.partyId ? party(next.partyId)?.partySize : null };
  if (!hit) return { ...base, status: 'free', partyId: null, partySize: null, blocked: false, held: false, elapsedMinutes: null, expectedMinutes: null };
  if (hit.kind === 'block') return { ...base, status: 'blocked', partyId: null, blocked: true, held: false, elapsedMinutes: null };
  const p = hit.partyId ? party(hit.partyId) : null;
  return { ...base, status: p?.status ?? 'booked', partyId: p?.id ?? null, partySize: p?.partySize ?? null, blocked: false, held: false, elapsedMinutes: null, expectedMinutes: null };
}

function floorView(): string {
  const d = D();
  const target = S.mode && ['seat', 'assign', 'move', 'waitseat', 'walkin'].includes(S.mode.kind);
  const servers = new Map<string, any>(d.servers.map((s: any) => [s.id, s]));
  const now = new Date(d.now).getTime();
  const plans: { id: string; name: string }[] = d.floorPlans ?? [];
  if (!plans.some((p) => p.id === S.planId)) S.planId = plans[0]?.id ?? '';
  const multi = plans.length > 1;
  const shownTables: any[] = d.tables.map((t: any) => (S.asOf == null ? t : tableAsOf(t, S.asOf as number)));
  const floorTables: any[] = multi ? shownTables.filter((t: any) => t.floorPlanId === S.planId) : shownTables;
  const floorFeatures: any[] = multi ? (d.features ?? []).filter((f: any) => f.floorPlanId === S.planId) : (d.features ?? []);
  const planTabs = multi
    ? `<div class="plan-tabs" role="tablist" aria-label="${esc(T('h.plans_aria'))}">${plans
        .map((p) => {
          const mine = d.tables.filter((t: any) => t.floorPlanId === p.id);
          const busy = mine.filter((t: any) => t.status !== 'free' && t.status !== 'blocked' && t.status !== 'held').length;
          return `<button role="tab" data-act="plan" data-id="${p.id}" aria-selected="${p.id === S.planId}">${esc(p.name)} <span class="muted small">${busy}/${mine.length}</span></button>`;
        })
        .join('')}</div>`
    : '';
  const clock =
    `<button class="asof-btn ${S.asOf != null ? 'on' : ''}" data-act="as-of" title="${esc(T('h.y.asof_title'))}">🕒 ${S.asOf != null ? esc(hhmm(S.asOf)) : esc(T('h.y.asof_now'))}</button>`;
  const boxes = floorTables
    .map((t: any) => {
      const { w, h } = tableBox(t);
      const info = t.status !== 'free' && t.status !== 'held' && t.status !== 'blocked' ? statusInfo(t.status) : null;
      const bg = info ? `background:${info.color};` : '';
      const srv = t.serverId ? servers.get(t.serverId) : null;
      const soonMin = t.nextStartsAt ? (new Date(t.nextStartsAt).getTime() - now) / 60000 : null;
      const soon = soonMin != null && soonMin <= 120 && !info;
      const sub = info
        ? `${t.partySize ?? ''} · ${t.elapsedMinutes != null ? minutesToLabel(t.elapsedMinutes) : ''}`
        : t.blocked
          ? T('h.tbl.blocked')
          : t.held
            ? T('h.tbl.held')
            : t.nextPartyName
              ? `${esc(t.nextPartyName.split(' ')[0])} ${t.nextPartySize}`
              : `${t.minSeats}-${t.maxSeats}`;
      const prog =
        info && t.expectedMinutes && t.elapsedMinutes != null
          ? `<span class="prog"><i style="width:${Math.min(100, Math.round((t.elapsedMinutes / t.expectedMinutes) * 100))}%"></i></span>`
          : '';
      const sel = S.sel?.kind === 'table' && S.sel.id === t.id;
      const rot = t.rotation ? `transform:rotate(${t.rotation}deg);` : '';
      const upright = t.rotation ? ` style="transform:rotate(${-t.rotation}deg)"` : '';
      return `
      <div class="tbl-box ${t.shape === 'round' ? 'round' : ''} ${t.blocked ? 'blocked' : ''} ${t.held ? 'held' : ''} ${soon ? 'soon' : ''} ${sel ? 'sel' : ''} ${target && t.status === 'free' ? 'target' : ''}"
           role="button" tabindex="0" data-act="sel-table" data-id="${t.id}"
           data-next="${t.nextStartsAt ? tm(t.nextStartsAt) : ''}"
           aria-label="${esc(T('h.tbl.aria', { label: t.label, state: info ? statusLabel(t.status) : (T(`h.tbl.${t.status}`) === `h.tbl.${t.status}` ? t.status : T(`h.tbl.${t.status}`)) }))}"
           style="left:${t.x}px;top:${t.y}px;width:${w}px;height:${h}px;${rot}${bg}">
        ${srv ? `<span class="srv" style="background:${srv.color}" title="${esc(srv.name)}">${esc(srv.initials)}</span>` : ''}
        <span class="txt"${upright}><span class="lab">${esc(t.label)}</span>
        <span class="sub">${sub}</span></span>
        ${prog}
      </div>`;
    })
    .join('');
  const fixtures = floorFeatures
    .map((f: any) => {
      const rot = f.rotation ? `transform:rotate(${f.rotation}deg);` : '';
      return `<div class="fx fx-${f.kind}" aria-hidden="true" style="left:${f.x}px;top:${f.y}px;width:${f.w}px;height:${f.h}px;${rot}">${
        f.kind === 'label' ? `<span>${esc(f.text ?? '')}</span>` : f.kind === 'door' ? '<i></i>' : ''
      }</div>`;
    })
    .join('');
  const banner = S.mode ? modeBanner() : '';
  // Size the floor to the tables so a large plan scrolls and a small one is not padded.
  const right = Math.max(0, ...floorTables.map((t: any) => t.x + tableBox(t).w), ...floorFeatures.map((f: any) => f.x + f.w));
  const bottom = Math.max(0, ...floorTables.map((t: any) => t.y + tableBox(t).h), ...floorFeatures.map((f: any) => f.y + f.h));
  return `<main class="stage" tabindex="0">${banner}${planTabs}${clock}<div class="floor" style="min-width:${right + 40}px;min-height:${bottom + 40}px">${fixtures}${boxes}</div></main>`;
}

function modeBanner(): string {
  const m = S.mode!;
  let text = '';
  const who = (id: string) => party(id)?.name ?? T('h.party_fallback');
  if (m.kind === 'seat') text = T('h.banner.seat', { name: who(m.partyId) });
  else if (m.kind === 'assign') text = T('h.banner.assign', { name: who(m.partyId) });
  else if (m.kind === 'move') text = T('h.banner.move', { name: who(m.partyId) });
  else if (m.kind === 'waitseat') text = T('h.banner.waitseat');
  else if (m.kind === 'walkin') text = T('h.banner.walkin', { n: m.partySize });
  return `<div class="banner"><strong>${esc(text)}</strong>${['seat', 'move', 'waitseat', 'walkin'].includes(m.kind) ? nextToSeatPanel() : ''}<span class="spacer"></span><button data-act="cancel-mode">${esc(T('common.cancel'))}</button></div>`;
}

function listView(): string {
  const d = D();
  const col = (title: string, key: string, rows: string, t: any) =>
    `<section class="col"><h3><span>${esc(title)}</span><span class="muted">${t.parties} · ${esc(T('common.ppl', { n: t.covers }))}</span></h3>${rows || `<div class="party"><span></span><span class="muted small" style="grid-column:2/-1">${esc(T('h.none'))}</span></div>`}</section>`;
  return `<main class="stage" tabindex="0"><div class="cols">
    ${col(T('h.group.waitlist'), 'w', d.parties.waitlist.map(waitRow).join(''), d.totals.waitlist)}
    ${col(T('h.group.reservations'), 'r', d.parties.reservations.map(partyRow).join(''), d.totals.reservations)}
    ${col(T('h.group.seated'), 's', d.parties.seated.map(partyRow).join(''), d.totals.seated)}
    ${col(T('h.group.finished'), 'f', d.parties.finished.map(partyRow).join(''), d.totals.finished)}
    ${col(T('h.group.removed'), 'x', d.parties.removed.map(partyRow).join(''), d.totals.removed)}
  </div></main>`;
}

function timelineView(): string {
  const d = D();
  const shift = d.shifts.find((s: any) => s.id === d.shiftId);
  if (!shift) return `<main class="stage" tabindex="0"><p class="loading">${esc(T('h.tl.no_shift'))}</p></main>`;
  const T0 = Math.floor((shift.first - 30) / 30) * 30;
  const T1 = Math.ceil((shift.last + 180) / 30) * 30;
  const PX = 1.7;
  const width = (T1 - T0) * PX;
  const marks: string[] = [];
  for (let m = T0; m <= T1; m += 30) marks.push(`<span style="left:${(m - T0) * PX}px">${m % 60 === 0 ? (getLocale() === 'de' ? `${Math.floor(m / 60) % 24}` : `${((Math.floor(m / 60) + 11) % 12) + 1}${Math.floor(m / 60) % 24 < 12 ? 'a' : 'p'}`) : ':30'}</span>`);
  const grid = `background-size:${30 * PX}px 100%;`;
  const nowMin = d.date === todayIn(S.tz) ? localMinutes(d.now) : null;
  const rows = d.tables
    .map((t: any) => {
      const bars = t.bookings
        .map((b: any) => {
          const rawS = minutesOnShownDate(b.startsAt);
          const rawE = minutesOnShownDate(b.endsAt);
          if (rawE <= T0 || rawS >= T1) return '';
          const s = Math.max(T0, rawS);
          const e = Math.min(T1, rawE);
          const p = b.partyId ? party(b.partyId) : null;
          const color = b.kind === 'block' ? '#4b5563' : (statusInfo(p?.status ?? 'booked')?.color ?? '#6b7280');
          const label = b.kind === 'block' ? T('h.tl.blocked') : `${esc(p?.name ?? T('h.tl.booked'))} · ${p?.partySize ?? ''}`;
          return `<div class="tl-bar" data-act="${p ? 'sel-party' : 'noop'}" data-id="${p?.id ?? ''}" style="left:${(s - T0) * PX}px;width:${Math.max(24, (e - s) * PX)}px;background:${color};color:${inkOn(color)}" title="${esc(label)}">${label}</div>`;
        })
        .join('');
      return `<div class="tl-row"><div class="tl-label">${esc(t.label)}<small>${t.minSeats}-${t.maxSeats}</small></div>
        <div class="tl-track" style="width:${width}px;${grid}" data-act="tl-click" data-table="${t.id}" data-t0="${T0}" data-px="${PX}">${bars}${nowMin != null && nowMin >= T0 && nowMin <= T1 ? `<span class="tl-now" style="left:${(nowMin - T0) * PX}px"></span>` : ''}</div></div>`;
    })
    .join('');
  const cells = d.pacing
    .map((p: any) => {
      const m = Number(p.localTime.slice(0, 2)) * 60 + Number(p.localTime.slice(3));
      const full = p.maxCovers != null && p.covers >= p.maxCovers;
      const text = p.covers === 0 ? '·' : `${p.covers}${p.maxCovers != null ? '/' + p.maxCovers : ''}`;
      return `<span class="cell ${full ? 'full' : ''}" style="left:${(m - T0 + 7) * PX}px" title="${esc(p.maxCovers != null ? T('h.tl.cell_title', { c: p.covers, m: p.maxCovers, p: p.parties }) : T('h.tl.cell_title_free', { c: p.covers, p: p.parties }))}">${text}</span>`;
    })
    .join('');
  return `<main class="stage" tabindex="0"><div class="tl">
    <div class="tl-head"><div class="tl-label">${esc(T('h.tl.table'))}</div><div class="tl-axis" style="width:${width}px">${marks.join('')}</div></div>
    ${rows}
    <div class="tl-foot"><div class="tl-label">${esc(T('h.tl.covers'))}<small>${esc(T('h.tl.cap'))}</small></div><div class="cells" style="width:${width}px">${cells}</div></div>
  </div></main>`;
}

function calendarView(): string {
  const c = S.cal;
  if (!c) return `<main class="stage" tabindex="0"><p class="loading">${esc(T('common.loading'))}</p></main>`;
  const [y, m] = c.month.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Monday first
  const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const by = new Map<string, any>(c.days.map((d: any) => [d.day, d]));
  const today = todayIn(S.tz);
  const wd = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 1 + i))));
  const cells: string[] = [];
  for (let i = 0; i < lead; i += 1) cells.push('<div class="cal-cell empty"></div>');
  for (let day = 1; day <= total; day += 1) {
    const iso = `${c.month}-${String(day).padStart(2, '0')}`;
    const d = by.get(iso);
    const sum = d ? d.regular + d.walkIn + d.experiences : 0;
    cells.push(`<button class="cal-cell ${d?.closed ? 'closed' : ''} ${iso === today ? 'today' : ''} ${iso === S.date ? 'sel' : ''}" data-act="cal-day" data-day="${iso}">
      <span class="dn">${day}</span>
      ${d?.closed ? `<span class="tag">${esc(d.title || T('h.closed'))}</span>` : sum ? `<strong>${sum}</strong>` : '<span class="muted">·</span>'}
      ${d && sum ? `<span class="split small">${d.regular} · ${d.walkIn}${d.experiences ? ` · ★${d.experiences}` : ''}</span>` : ''}
      ${d?.title && !d.closed ? `<span class="tag">${esc(d.title)}</span>` : ''}</button>`);
  }
  const label = new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(first);
  return `<main class="stage" tabindex="0"><div class="calendar">
    <div class="cal-head"><button data-act="cal-nav" data-n="-1" aria-label="${esc(T('h.prev_day'))}">‹</button><strong>${esc(label)}</strong>
      <button data-act="cal-nav" data-n="1" aria-label="${esc(T('h.next_day'))}">›</button>
      <span class="muted small">${esc(T('h.y.cal_legend'))}</span></div>
    <div class="cal-grid">${wd.map((w) => `<div class="cal-wd">${esc(w)}</div>`).join('')}${cells.join('')}</div></div></main>`;
}

async function feedModal() {
  let items: any[] = [];
  try {
    items = (await api<{ items: any[] }>('GET', `/v1/restaurants/${S.rid}/feed?limit=60`)).items;
  } catch (e) {
    toast(errMsg(e), true);
    return;
  }
  const label = (a: string) => ({ 'reservation.create': T('h.y.f.create'), 'reservation.modify': T('h.y.f.modify'), 'reservation.cancel': T('h.y.f.cancel'), 'reservation.no_show': T('h.y.f.noshow') } as Record<string, string>)[a] ?? a;
  const when = (iso: string) => `${fmtDate(localDate(iso))} ${tm(iso)}`;
  const rows = items
    .map((i) => {
      const moved = i.from
        ? `<span class="small"><s>${esc(when(i.from.startsAt))} · ${i.from.party}</s> → ${esc(when(i.startsAt))} · ${i.partySize}</span>`
        : `<span class="small">${esc(when(i.startsAt))} · ${i.partySize}</span>`;
      return `<button class="feed-row ${i.action === 'reservation.cancel' ? 'cancel' : ''}" data-act="search-pick" data-id="${i.reservationId}" data-date="${esc(localDate(i.startsAt))}">
        <span class="small muted">${esc(tm(i.at))} · ${esc(i.online ? T('h.y.f.online') : i.actor)}</span>
        <span><strong>${esc(i.name || i.code)}</strong> · ${esc(label(i.action))}</span>${moved}</button>`;
    })
    .join('');
  openModal(
    `<h2>${esc(T('h.y.feed'))}</h2><p class="muted small">${esc(T('h.y.feed_hint'))}</p>
    <div class="feed">${rows || `<p class="muted">${esc(T('h.y.feed_none'))}</p>`}</div>
    <div class="row-end"><button data-act="feed-seen">${esc(T('h.y.mark_seen'))}</button><button data-act="close-modal">${esc(T('common.close'))}</button></div>`,
    (root) => {
      root.addEventListener('click', (ev) => {
        const b = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
        if (b?.dataset.act === 'feed-seen') {
          try {
            localStorage.setItem(feedKey(), new Date().toISOString());
          } catch {
            /* ignore */
          }
          S.feedUnseen = 0;
          closeModal();
          render();
        } else if (b?.dataset.act === 'search-pick') closeModal();
      });
    },
  );
}

function problemsModal() {
  const list: any[] = D().problems ?? [];
  const label = (p: any) =>
    p.kind === 'unassigned' ? T('h.y.p.unassigned')
      : p.kind === 'late' ? T('h.y.p.late', { n: p.minutes })
      : p.kind === 'overdue' ? T('h.y.p.overdue', { n: p.minutes })
      : T('h.y.p.too_big');
  const rows = list
    .map(
      (p) => `<button class="feed-row" data-act="sel-party" data-id="${p.partyId}">
        <span class="small muted">${esc(tm(p.time))}</span><span><strong>${esc(p.name)}</strong> · ${p.partySize}</span><span class="small over">${esc(label(p))}</span></button>`,
    )
    .join('');
  openModal(
    `<h2>${esc(T('h.y.problems'))}</h2><div class="feed">${rows || `<p class="muted">${esc(T('h.y.problems_none'))}</p>`}</div>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button></div>`,
    (root) => {
      root.addEventListener('click', (ev) => {
        if ((ev.target as HTMLElement).closest('[data-act="sel-party"]')) closeModal();
      });
    },
  );
}

async function auditModal() {
  let items: any[] = [];
  try {
    items = (await api<{ items: any[] }>('GET', `/v1/restaurants/${S.rid}/audit?limit=150`)).items;
  } catch (e) {
    toast(errMsg(e), true);
    return;
  }
  const rows = items
    .map(
      (i) => `<tr><td class="small">${esc(fmtDate(localDate(i.at)))} ${esc(tm(i.at))}</td><td>${esc(i.actor)}</td><td>${esc((T(`h.x.ev.${i.action}`) === `h.x.ev.${i.action}` ? i.action : T(`h.x.ev.${i.action}`)))}</td>
        <td class="small muted">${esc(i.subject ? String(i.subject).slice(0, 8) : '')}</td></tr>`,
    )
    .join('');
  openModal(
    `<h2>${esc(T('h.y.audit'))}</h2><p class="muted small">${esc(T('h.y.audit_hint'))}</p>
    <div class="feed"><table class="cfg-table" style="width:100%"><thead><tr><th>${esc(T('h.y.when'))}</th><th>${esc(T('h.y.who'))}</th><th>${esc(T('h.y.what'))}</th><th>${esc(T('h.y.ref'))}</th></tr></thead><tbody>${rows}</tbody></table></div>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button></div>`,
  );
}

function asOfModal() {
  const cur = S.asOf != null ? hhmm(S.asOf) : '';
  openModal(
    `<h2>${esc(T('h.y.asof_title'))}</h2><p class="muted small">${esc(T('h.y.asof_hint'))}</p>
    <label for="asof-t">${esc(T('h.book.time'))}</label><select id="asof-t">${timeOptions(cur || hhmm(19 * 60))}</select>
    <div class="row-end"><button data-act="asof-now">${esc(T('h.y.asof_now'))}</button><button class="primary" data-act="asof-set">${esc(T('h.y.asof_show'))}</button></div>`,
    (root) => {
      root.addEventListener('click', (ev) => {
        const b = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
        if (!b) return;
        if (b.dataset.act === 'asof-now') S.asOf = null;
        else if (b.dataset.act === 'asof-set') {
          const v = (root.querySelector('#asof-t') as HTMLSelectElement).value;
          S.asOf = Number(v.slice(0, 2)) * 60 + Number(v.slice(3, 5));
        } else return;
        closeModal();
        render();
      });
    },
  );
}

/** A list of every status that is switched on, with its colour, the current one ticked. */
function statusPicker(partyId: string) {
  const p = party(partyId);
  if (!p) return;
  const phase = p.group === 'seated' ? 'seated' : 'pre';
  const rows = (D().statuses as any[])
    .filter((x) => x.phase === phase && x.enabled && !['cancelled', 'no_show'].includes(x.code))
    .map(
      (x) => `<button class="pick-row" data-act="pick-status" data-s="${x.code}"><i style="background:${x.color}"></i>${esc(statusLabel(x.code))}${x.code === p.status ? '<span class="tick">✓</span>' : ''}</button>`,
    )
    .join('');
  openModal(
    `<h2>${esc(T('h.y.update_status'))}</h2><div class="feed">${rows}</div><div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button></div>`,
    (root) => {
      root.addEventListener('click', async (ev) => {
        const b = (ev.target as HTMLElement).closest('[data-act="pick-status"]') as HTMLElement | null;
        if (!b) return;
        closeModal();
        await act(() => api('POST', `/v1/host/reservations/${partyId}/status`, { status: b.dataset.s }), T('h.toast.status'));
      });
    },
  );
}

function overviewView(): string {
  const o = D().overview;
  const bars = (entries: [string, number][]) => {
    const max = Math.max(1, ...entries.map((e) => e[1]));
    return entries.length
      ? entries.map(([k, v]) => `<div class="hbar"><span>${esc(k)}</span><span class="track"><span class="fill" style="width:${(v / max) * 100}%;display:block"></span></span><span>${v}</span></div>`).join('')
      : `<p class="muted">${esc(T('h.ov.no_data'))}</p>`;
  };
  const sizes = Object.entries(o.partiesBySize as Record<string, number>).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
  const t = D().totals;
  return `<main class="stage" tabindex="0"><div class="overview">
    <section class="card"><h3>${esc(T('h.ov.shift'))}</h3>
      <div class="hbar"><span>${esc(T('h.ov.covers'))}</span><span></span><span><strong>${t.covers}</strong></span></div>
      <div class="hbar"><span>${esc(T('h.ov.booked'))}</span><span></span><span>${t.reservations.covers}</span></div>
      <div class="hbar"><span>${esc(T('h.ov.seated'))}</span><span></span><span>${t.seated.covers}</span></div>
      <div class="hbar"><span>${esc(T('h.ov.waitlist'))}</span><span></span><span>${t.waitlist.covers}</span></div>
    </section>
    <section class="card"><h3>${esc(T('h.ov.by_time'))}</h3>${bars(o.coversByTime.map((x: any) => [x.time, x.covers]))}</section>
    <section class="card"><h3>${esc(T('h.ov.by_source'))}</h3>${bars((Object.entries(o.coversBySource) as [string, number][]).map(([k, v]) => [sourceLabel(k), v] as [string, number]))}</section>
    <section class="card"><h3>${esc(T('h.ov.by_size'))}</h3>${bars(sizes)}</section>
    <section class="card"><h3>${esc(T('h.ov.servers'))}</h3>${D().servers
      .map((s: any) => `<div class="hbar"><span style="background:${s.color};color:${inkOn(s.color)};font-weight:700;padding:1px 6px;border-radius:6px">${esc(s.initials)}</span><span class="muted small">${esc(s.name)}</span><span>${s.parties}/${s.covers}</span></div>`)
      .join('')}</section>
  </div></main>`;
}

// ------------------------------------------------------------------ drawer

function drawer(): string {
  if (!S.sel) return '';
  if (S.sel.kind === 'party') return partyDrawer(party(S.sel.id));
  if (S.sel.kind === 'wait') return waitDrawer(D().parties.waitlist.find((w: any) => w.id === S.sel!.id));
  return tableDrawer(table(S.sel.id));
}

function statusOptions(p: any): string {
  const phase = p.group === 'seated' ? 'seated' : 'pre';
  return D()
    .statuses.filter((s: any) => s.phase === phase && s.enabled && !['cancelled', 'no_show'].includes(s.code))
    .map((s: any) => `<option value="${s.code}" ${s.code === p.status ? 'selected' : ''}>${esc(statusLabel(s.code))}</option>`)
    .join('');
}

function money(minor: number): string {
  return new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: D()?.restaurant?.currency ?? 'USD' }).format(minor / 100);
}

/** Card / deposit state for a reservation, with the staff actions that apply. */
function paymentRows(p: any): string {
  const pay = p.payment;
  if (!pay) return '';
  const label: Record<string, string> = {
    pending: T('h.pay.pending'),
    card_saved: T('h.pay.card_saved'),
    paid: pay.kind === 'deposit' ? T('h.pay.deposit_paid', { amount: money(pay.amountMinor) }) : T('h.pay.fee_collected'),
    forfeited: T('h.pay.forfeited', { amount: money(pay.amountMinor) }),
    released: T('h.pay.released'),
    refunded: T('h.pay.refunded'),
    failed: T('h.pay.failed'),
    canceled: T('h.pay.canceled'),
  };
  const due =
    pay.dueAction === 'charge'
      ? `<span class="chip" style="border-color:var(--warn);color:var(--warn)">${esc(T('h.pay.fee_due', { amount: money(pay.dueMinor) }))}</span>
         ${can('payments.manage') ? `<button data-act="pay-charge" data-id="${p.id}" data-due="${pay.dueMinor}">${esc(T('h.pay.charge'))}</button>` : ''}`
      : pay.dueAction === 'refund'
        ? `<span class="chip">${esc(T('h.pay.refund_pending', { amount: money(pay.dueMinor) }))}</span>`
        : '';
  const canRefund = can('payments.manage') && pay.kind === 'deposit' && pay.status === 'forfeited';
  return `<dt>${esc(T('h.d.payment'))}</dt><dd><span class="chip">${esc(label[pay.status] ?? pay.status)}</span> ${due}
    ${canRefund ? `<button class="ghost" data-act="pay-refund" data-id="${p.id}" data-left="${pay.amountMinor}">${esc(T('h.pay.refund'))}</button>` : ''}</dd>`;
}

function partyDrawer(p: any): string {
  if (!p) return '';
  const seated = p.group === 'seated';
  const pre = p.group === 'reservations';
  const closeBtn = `<button class="ghost" data-act="close-drawer" aria-label="${esc(T('common.close'))}">✕</button>`;
  return `
  <aside class="drawer" aria-label="${esc(T('h.d.aria'))}">
    <div style="display:flex;justify-content:space-between;align-items:start">
      <div><h2>${esc(p.name)}</h2><span class="muted">${esc(TN('common.guests', p.partySize))} · ${tm(p.startsAt)} · ${esc(sourceLabel(p.source))}</span></div>${closeBtn}
    </div>
    ${
      (pre || seated) && can('reservations.manage')
        ? `<div class="chips-row" role="group" aria-label="${esc(T('h.y.change'))}">
            <button data-act="edit" data-id="${p.id}" title="${esc(T('h.y.change_time'))}">🕒 ${tm(p.startsAt)}</button>
            <button data-act="edit" data-id="${p.id}" title="${esc(T('h.y.change_party'))}">👥 ${p.partySize}</button>
            <button data-act="edit" data-id="${p.id}" title="${esc(T('h.y.change_duration'))}">⏱ ${minutesToLabel(p.durationMinutes)}</button></div>`
        : ''
    }
    <dl class="kv">
      <dt>${esc(T('h.d.status'))}</dt><dd>${
        (pre || seated) && can('host.seat')
          ? `<button class="status-btn" data-act="status-pick" data-id="${p.id}" style="--c:${statusInfo(p.status)?.color ?? '#8b95a5'}">${esc(statusLabel(p.status))} ▾</button>`
          : esc(statusLabel(p.status))
      }</dd>
      <dt>${esc(T('h.d.table'))}</dt><dd>${p.tableLabels.length ? esc(p.tableLabels.join(' + ')) : `<span class="muted">${esc(T('h.not_assigned'))}</span>`}</dd>
      <dt>${esc(T('h.d.duration'))}</dt><dd>${minutesToLabel(p.durationMinutes)}${p.elapsedMinutes != null ? ` · ${esc(T('h.d.elapsed', { time: minutesToLabel(p.elapsedMinutes) }))}` : ''}</dd>
      ${p.phone ? `<dt>${esc(T('h.d.phone'))}</dt><dd>${esc(p.phone)}</dd>` : ''}
      ${p.occasion ? `<dt>${esc(T('h.d.occasion'))}</dt><dd>${esc(occasionLabel(p.occasion))}</dd>` : ''}
      ${p.specialRequest ? `<dt>${esc(T('h.d.request'))}</dt><dd>${esc(p.specialRequest)}</dd>` : ''}
      ${p.visitNote ? `<dt>${esc(T('h.x.visit_note'))}</dt><dd>${esc(p.visitNote)}</dd>` : ''}
      <dt>${esc(T('h.x.referral'))}</dt><dd>${
        can('reservations.manage')
          ? `<input class="inline-edit" id="referral" data-id="${p.id}" maxlength="120" value="${esc(p.referral ?? '')}" placeholder="${esc(T('h.x.referral_ph'))}" aria-label="${esc(T('h.x.referral'))}">`
          : esc(p.referral ?? '—')
      }</dd>
      ${p.tags?.length ? `<dt>${esc(T('h.x.tags'))}</dt><dd>${p.tags.map((t: string) => `<span class="chip">${esc(t)}</span>`).join(' ')}</dd>` : ''}
      ${p.policyKind && p.policyKind !== 'none' ? `<dt>${esc(T('h.d.policy'))}</dt><dd><span class="chip">${esc(T(`h.policy.${p.policyKind}`) === `h.policy.${p.policyKind}` ? p.policyKind : T(`h.policy.${p.policyKind}`))}</span></dd>` : ''}
      ${paymentRows(p)}
      <dt>${esc(T('h.d.code'))}</dt><dd>${esc(p.code)}</dd>
    </dl>
    <div class="actions">
      ${pre && can('host.seat') ? `<button class="primary" data-act="seat" data-id="${p.id}">${esc(p.tableIds.length ? T('h.a.seat_now') : T('h.a.seat'))}</button>` : ''}
      ${pre && can('host.seat') ? `<button data-act="assign" data-id="${p.id}">${esc(T('h.a.preassign'))}</button>` : ''}
      ${seated && can('host.seat') ? `<button data-act="move" data-id="${p.id}">${esc(T('h.a.move'))}</button><button class="primary" data-act="finish" data-id="${p.id}">${esc(T('h.a.finish'))}</button>` : ''}
      ${(pre || seated) && can('reservations.manage') ? `<button data-act="edit" data-id="${p.id}">${esc(T('h.a.edit'))}</button>` : ''}
      ${D().restaurant.smsEnabled && (pre || seated) && can('host.seat') && p.phone && p.smsOptIn ? `<button data-act="sms" data-id="${p.id}" data-kind="table_ready">${esc(T('h.x.sms_ready'))}</button><button data-act="sms" data-id="${p.id}" data-kind="custom">${esc(T('h.x.sms_custom'))}</button>` : ''}
      ${D().restaurant.smsEnabled && (pre || seated) && can('host.seat') && p.phone && !p.smsOptIn ? `<span class="muted small">${esc(T('h.x.sms_no_consent'))}</span>` : ''}
      ${pre && can('host.seat') ? `<button class="danger" data-act="noshow" data-id="${p.id}">${esc(T('h.a.noshow'))}</button><button class="danger" data-act="cancel-res" data-id="${p.id}">${esc(T('h.a.cancel'))}</button>` : ''}
    </div>
    ${
      can('pacing.override')
        ? `<label><input type="checkbox" style="width:auto" data-act="exclude" data-id="${p.id}" ${p.excludeFromPacing ? 'checked' : ''}> ${esc(T('h.d.exclude'))}</label>`
        : p.excludeFromPacing
          ? `<p class="muted small">${esc(T('h.d.excluded'))}</p>`
          : ''
    }
    ${can('guests.manage') && p.guestId ? `<h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.x.guestbook'))}</h3><div id="guestbook" class="small muted">${esc(T('common.loading'))}</div>` : ''}
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.x.history'))}</h3>
    <div id="history" class="small muted">${esc(T('common.loading'))}</div>
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.d.emails'))}</h3>
    <div id="emails" class="small muted">${esc(T('common.loading'))}</div>
    ${can('reservations.manage') && (pre || seated) ? `<div class="row-end"><button data-act="resend-email" data-id="${p.id}">${esc(T('h.d.resend'))}</button></div>` : ''}
    ${can('staff.manage') ? `<div class="row-end"><button class="danger" data-act="erase-guest" data-id="${p.id}">${esc(T('h.d.erase'))}</button></div>` : ''}
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.d.notes'))}</h3>
    <div id="notes" class="small muted">${esc(p.noteCount ? T('common.loading') : T('h.d.no_notes'))}</div>
    ${
      can('host.seat')
        ? `<div class="grid2" style="margin-top:8px">
      <select id="note-scope" aria-label="${esc(T('h.n.type'))}">${['general', 'relationship', 'food_drink', 'seating', 'visit'].map((k) => `<option value="${k}">${esc(T(`h.n.${k}`))}</option>`).join('')}</select>
      <input id="note-body" placeholder="${esc(T('h.n.placeholder'))}" aria-label="${esc(T('h.n.text'))}">
    </div>
    <div class="row-end"><button data-act="add-note" data-id="${p.id}">${esc(T('h.n.add'))}</button></div>`
        : ''
    }
  </aside>`;
}

function waitDrawer(w: any): string {
  if (!w) return '';
  return `
  <aside class="drawer" aria-label="${esc(T('h.w.aria'))}">
    <div style="display:flex;justify-content:space-between"><div><h2>${esc(w.name)}</h2><span class="muted">${esc(TN('common.guests', w.partySize))}</span></div><button class="ghost" data-act="close-drawer" aria-label="${esc(T('common.close'))}">✕</button></div>
    <dl class="kv">
      <dt>${esc(T('h.w.quoted'))}</dt><dd>${esc(T('h.w.minutes', { n: w.quotedMinutes }))}</dd>
      <dt>${esc(T('h.w.waiting'))}</dt><dd class="${w.overQuote ? 'over' : ''}">${esc(T('h.w.minutes', { n: w.waitedMinutes }))}</dd>
      ${w.phone ? `<dt>${esc(T('h.d.phone'))}</dt><dd>${esc(w.phone)}</dd>` : ''}
      <dt>${esc(T('h.d.status'))}</dt><dd>${esc(T(`h.w.status.${w.status}`) === `h.w.status.${w.status}` ? w.status : T(`h.w.status.${w.status}`))}</dd>
    </dl>
    <div class="actions">
      ${
        can('waitlist.manage')
          ? `<button data-act="wait-notify" data-id="${w.id}">${esc(T('h.w.notify'))}</button>
      <button class="primary" data-act="wait-seat" data-id="${w.id}">${esc(T('h.w.seat'))}</button>
      <button class="danger" data-act="wait-remove" data-id="${w.id}">${esc(T('h.w.remove'))}</button>`
          : `<span class="muted small">${esc(T('h.w.no_permission'))}</span>`
      }
    </div>
  </aside>`;
}

/** One-tap status changes for a seated party, in the order a meal usually goes. */
function quickStatuses(p: any): string {
  const order = ['partially_seated', 'seated', 'drinks', 'appetizer', 'entree', 'dessert', 'check_dropped', 'paid', 'bussing_needed', 'cleared'];
  const enabled = new Map<string, any>((D().statuses as any[]).filter((s) => s.phase === 'seated' && s.enabled).map((s) => [s.code, s]));
  return order
    .filter((c) => enabled.has(c))
    .map(
      (c) => `<button data-act="quick-status" data-id="${p.id}" data-s="${c}" aria-pressed="${p.status === c}" style="--c:${enabled.get(c).color}">${esc(statusLabel(c))}</button>`,
    )
    .join('');
}

/** A small strip of the shift with the table's bookings drawn on it. */
function scheduleStrip(t: any): string {
  const d = D();
  const shift = d.shifts.find((s: any) => s.id === d.shiftId);
  if (!shift) return '';
  const T0 = Math.floor((shift.first - 30) / 30) * 30;
  const T1 = Math.ceil((shift.last + 150) / 30) * 30;
  const span = T1 - T0;
  const blocks = (t.bookings as any[])
    .map((b) => {
      const s0 = Math.max(T0, minutesOnShownDate(b.startsAt));
      const e0 = Math.min(T1, minutesOnShownDate(b.endsAt));
      if (e0 <= s0) return '';
      const p = b.partyId ? party(b.partyId) : null;
      const color = b.kind === 'block' ? '#4b5563' : (statusInfo(p?.status ?? 'booked')?.color ?? '#6b7280');
      return `<i style="left:${((s0 - T0) / span) * 100}%;width:${((e0 - s0) / span) * 100}%;background:${color}" title="${esc(`${tm(b.startsAt)}–${tm(b.endsAt)} ${p?.name ?? ''}`)}"></i>`;
    })
    .join('');
  const nowMin = d.date === todayIn(S.tz) ? localMinutes(d.now) : null;
  const now = nowMin != null && nowMin >= T0 && nowMin <= T1 ? `<b style="left:${((nowMin - T0) / span) * 100}%"></b>` : '';
  const ticks: string[] = [];
  for (let m = Math.ceil(T0 / 60) * 60; m <= T1; m += 60) ticks.push(`<span style="left:${((m - T0) / span) * 100}%">${esc(hhmm(m))}</span>`);
  return `<div class="strip" aria-label="${esc(T('h.x.schedule'))}"><div class="bar">${blocks}${now}</div><div class="ticks">${ticks.join('')}</div></div>`;
}

function tableDrawer(t: any): string {
  if (!t) return '';
  const info = statusInfo(t.status);
  const p = t.partyId ? party(t.partyId) : null;
  return `
  <aside class="drawer" aria-label="${esc(T('h.t.aria'))}">
    <div style="display:flex;justify-content:space-between"><div><h2>${esc(T('h.table_n', { label: t.label }))}</h2><span class="muted">${esc(T('h.t.seats', { min: t.minSeats, max: t.maxSeats }))}${t.areaName ? ' · ' + esc(t.areaName) : ''}</span></div><button class="ghost" data-act="close-drawer" aria-label="${esc(T('common.close'))}">✕</button></div>
    ${
      p
        ? `<dl class="kv"><dt>${esc(T('h.t.party'))}</dt><dd>${esc(p.name)} (${p.partySize})</dd><dt>${esc(T('h.d.status'))}</dt><dd>${esc(info ? statusLabel(t.status) : t.status)}</dd><dt>${esc(T('h.t.elapsed'))}</dt><dd>${t.elapsedMinutes != null ? minutesToLabel(t.elapsedMinutes) : '—'}</dd></dl>
           <div class="actions"><button data-act="sel-party" data-id="${p.id}">${esc(T('h.t.open_party'))}</button><button class="primary" data-act="finish" data-id="${p.id}">${esc(T('h.a.finish'))}</button></div>
           ${can('host.seat') && p.group === 'seated' ? `<div class="quick-status" role="group" aria-label="${esc(T('h.x.status_quick'))}">${quickStatuses(p)}</div>` : ''}`
        : `<p class="muted">${esc(t.blocked ? T('h.t.blocked') : t.held ? T('h.t.held') : T('h.t.free'))}${t.nextStartsAt ? ` · ${esc(T('h.t.next', { time: tm(t.nextStartsAt) }))}${t.nextPartyName ? ' (' + esc(t.nextPartyName) + ')' : ''}` : ''}</p>
           <div class="actions">
             ${
               t.blocked
                 ? ''
                 : `${can('host.seat') ? `<button class="primary" data-act="walkin" data-id="${t.id}">${esc(T('h.t.walkin'))}</button>` : ''}${can('tables.block') ? `<button data-act="block" data-id="${t.id}">${esc(T('h.t.block'))}</button>` : ''}`
             }
           </div>`
    }
    ${scheduleStrip(t)}
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${S.date === todayIn(S.tz) ? esc(T('h.today')) : esc(fmtDate(S.date))}</h3>
    ${
      t.bookings.length
        ? t.bookings
            .map((b: any) => {
              const bp = b.partyId ? party(b.partyId) : null;
              return `<div class="party" style="grid-template-columns:4px 1fr"><span class="bar" style="background:${b.kind === 'block' ? '#4b5563' : (statusInfo(bp?.status ?? 'booked')?.color ?? '#6b7280')}"></span><span>${tm(b.startsAt)}–${tm(b.endsAt)} · ${b.kind === 'block' ? esc(T('h.t.blocked')) : esc(bp?.name ?? T('h.tl.booked'))}</span></div>`;
            })
            .join('')
        : `<p class="muted small">${esc(T('h.t.no_bookings'))}</p>`
    }
  </aside>`;
}

// ------------------------------------------------------------------ render

function render() {
  try {
    draw();
  } catch (e) {
    console.error(e);
    toast(T('h.render_failed', { error: errMsg(e) }), true);
  }
}

function draw() {
  if (!S.data) return;
  const d = S.data;
  const v = S.view;
  const hasDrawer = !!S.sel;
  const cls = `body ${v === 'floor' ? '' : 'single'} ${hasDrawer ? 'with-drawer' : ''}`;
  const main = v === 'floor' ? floorView() : v === 'list' ? listView() : v === 'timeline' ? timelineView() : v === 'calendar' ? calendarView() : overviewView();
  const prevScroll = (document.querySelector('main.stage') as HTMLElement | null)?.scrollTop ?? 0;
  const prevLists = (document.querySelector('aside.lists') as HTMLElement | null)?.scrollTop ?? 0;
  const ae = document.activeElement as HTMLInputElement | null;
  const keepFocus = ae?.id === 'host-search' ? { start: ae.selectionStart ?? ae.value.length, end: ae.selectionEnd ?? ae.value.length } : null;
  app.innerHTML = `${header()}<div class="${cls.trim()}">${v === 'floor' ? listsPanel() : ''}${main}${drawer()}</div>`;
  const stage = document.querySelector('main.stage') as HTMLElement | null;
  if (stage) stage.scrollTop = prevScroll;
  const lists = document.querySelector('aside.lists') as HTMLElement | null;
  if (lists) lists.scrollTop = prevLists;
  document.title = `${T('h.title')} · ${d.restaurant.name}`;
  if (keepFocus) {
    const si = document.getElementById('host-search') as HTMLInputElement | null;
    si?.focus();
    si?.setSelectionRange(keepFocus.start, keepFocus.end);
  }
  if (S.search.trim().length >= 2) void runSearch(S.search);
  if (S.sel?.kind === 'party') {
    void loadNotes(S.sel.id);
    void loadEmails(S.sel.id);
    void loadHistory(S.sel.id);
    const sp = party(S.sel.id);
    if (sp?.guestId && can('guests.manage')) void loadGuestbook(sp.guestId);
  }
}

const NOTE_KEYS = ['general', 'relationship', 'food_drink', 'seating'] as const;

/** Guest-level notes and tags: these follow the person from visit to visit. */
async function loadGuestbook(guestId: string) {
  const el = document.getElementById('guestbook');
  if (!el) return;
  try {
    const [g, cat] = await Promise.all([
      api<any>('GET', `/v1/restaurants/${S.rid}/guests/${guestId}`),
      api<any>('GET', `/v1/restaurants/${S.rid}/guest-tags`),
    ]);
    const have = new Set<string>(g.tags);
    el.innerHTML = `
      <div class="counters"><span><strong>${g.upcoming}</strong>${esc(T('h.x.upcoming'))}</span><span><strong>${g.visits}</strong>${esc(T('h.x.visits'))}</span>
        <span><strong>${g.cancellations}</strong>${esc(T('h.x.cancellations'))}</span><span><strong>${g.noShows}</strong>${esc(T('h.x.noshows'))}</span></div>
      <div class="tagpick">${(cat.tags as any[])
        .filter((t) => t.minVisits == null)
        .map((t) => `<label class="tagchip" style="--c:${esc(t.color)}"><input type="checkbox" data-tag="${esc(t.name)}" ${have.has(t.name) ? 'checked' : ''}> ${esc(t.name)}</label>`)
        .join('')}${(g.autoTags as string[]).map((t) => `<span class="chip">${esc(t)}</span>`).join(' ')}</div>
      ${NOTE_KEYS.map(
        (k) => `<label class="small">${esc(T(`h.n.${k}`))}<input class="inline-edit" data-gnote="${k}" maxlength="500" value="${esc(g.profileNotes?.[k] ?? '')}"></label>`,
      ).join('')}
      <div class="row-end"><button data-act="save-guestbook" data-gid="${guestId}">${esc(T('common.save'))}</button></div>`;
  } catch {
    el.textContent = '';
  }
}

async function loadHistory(id: string) {
  const el = document.getElementById('history');
  if (!el) return;
  try {
    const r = await api<{ history: any[] }>('GET', `/v1/host/reservations/${id}/history`);
    const label = (a: string) => {
      const k = `h.x.ev.${a}`;
      return T(k) === k ? a : T(k);
    };
    el.innerHTML = r.history.length
      ? r.history
          .map((h) => `<div>${esc(tm(h.at))} · ${esc(label(h.action))} <span class="muted">${esc(h.actor)}</span></div>`)
          .join('')
      : esc(T('h.x.no_history'));
  } catch {
    el.textContent = '';
  }
}

async function loadEmails(id: string) {
  const el = document.getElementById('emails');
  if (!el) return;
  const label: Record<string, string> = {
    confirmation: T('h.em.confirmation'), reminder: T('h.em.reminder'), change: T('h.em.change'), cancellation: T('h.em.cancellation'),
  };
  try {
    const r = await api<{ emails: any[] }>('GET', `/v1/host/reservations/${id}/emails`);
    el.innerHTML = r.emails.length
      ? r.emails
          .map((m) => {
            const when = m.sentAt ? esc(T('h.em.sent', { time: tm(m.sentAt) })) : m.status === 'failed' ? esc(T('h.em.failed', { error: m.error ?? '' })) : m.status === 'skipped' ? esc(T('h.em.skipped')) : esc(T('h.em.queued', { time: tm(m.sendAfter) }));
            return `<div><span class="chip">${esc(label[m.kind] ?? m.kind)}</span> ${esc(m.to)} · ${when}</div>`;
          })
          .join('')
      : esc(T('h.em.none'));
  } catch {
    el.textContent = '';
  }
}

async function loadNotes(id: string) {
  const el = document.getElementById('notes');
  if (!el) return;
  try {
    const r = await api<{ notes: { scope: string; body: string }[] }>('GET', `/v1/host/reservations/${id}/notes`);
    el.innerHTML = r.notes.length
      ? r.notes.map((n) => `<div><span class="chip">${esc(T(`h.note.${n.scope}`) === `h.note.${n.scope}` ? n.scope.replace('_', ' ') : T(`h.note.${n.scope}`))}</span>${esc(n.body)}</div>`).join('')
      : esc(T('h.d.no_notes'));
  } catch {
    el.textContent = '';
  }
}

// ------------------------------------------------------------------ modals

function openModal(html: string, onMount?: (root: HTMLElement) => void) {
  S.modalOpen = true;
  modalRoot.innerHTML = `<div class="scrim" data-act="scrim"><div class="modal" role="dialog" aria-modal="true">${html}</div></div>`;
  const first = modalRoot.querySelector('input,select,button') as HTMLElement | null;
  first?.focus();
  onMount?.(modalRoot);
}
function closeModal() {
  S.modalOpen = false;
  modalRoot.innerHTML = '';
}

const TIMES = (() => {
  const a: string[] = [];
  for (let m = 7 * 60; m <= 23 * 60 + 45; m += 15) a.push(hhmm(m));
  return a;
})();
const timeOptions = (sel: string) => TIMES.map((t) => `<option value="${t}" ${t === sel ? 'selected' : ''}>${t}</option>`).join('');

function bookModal(prefill: { date?: string; time?: string; party?: number; tableId?: string } = {}) {
  let pickedGuest: string | null = null;
  const shift = D().shifts.find((s: any) => s.id === D().shiftId);
  const defTime = prefill.time ?? (shift ? hhmm(Math.max(shift.first, Math.min(shift.last, 19 * 60))) : '19:00');
  let chosen = defTime;
  openModal(
    `<h2>${esc(T('h.book.title'))}</h2>
    <div class="grid3">
      <div><label for="b-date">${esc(T('h.book.date'))}</label><input id="b-date" type="date" value="${prefill.date ?? D().date}"></div>
      <div><label for="b-time">${esc(T('h.book.time'))}</label><select id="b-time">${timeOptions(defTime)}</select></div>
      <div><label for="b-party">${esc(T('h.book.party'))}</label><input id="b-party" type="number" min="1" max="40" value="${prefill.party ?? 2}"></div>
    </div>
    <div class="row-end" style="justify-content:flex-start;align-items:center"><button data-act="b-find">${esc(T('h.book.find'))}</button>
      ${
        can('pacing.override')
          ? `<label style="margin:0;display:flex;align-items:center;gap:6px"><input type="checkbox" id="b-over" style="width:auto"> ${esc(T('h.book.override'))}</label>
             <input id="b-pass" type="password" inputmode="numeric" maxlength="8" placeholder="${esc(T('h.pc.aria'))}" aria-label="${esc(T('h.book.override_pass'))}" style="width:120px;display:none">`
          : '<input type="checkbox" id="b-over" style="display:none">'
      }</div>
    <div id="b-times" class="times"></div>
    <div class="guest-find"><label for="b-search">${esc(T('h.x.find_guest'))}</label>
      <input id="b-search" type="search" autocomplete="off" placeholder="${esc(T('h.x.find_guest_ph'))}">
      <div id="b-guests" class="search-results"></div>
      <div id="b-picked" class="small"></div></div>
    <div class="grid2">
      <div><label for="b-first">${esc(T('h.book.first'))}</label><input id="b-first"></div>
      <div><label for="b-last">${esc(T('h.book.last'))}</label><input id="b-last"></div>
      <div><label for="b-phone">${esc(T('h.book.phone'))}</label><input id="b-phone" type="tel"></div>
      <div><label for="b-email">${esc(T('h.book.email'))}</label><input id="b-email" type="email"></div>
      <div><label for="b-company">${esc(T('g.company'))}</label><input id="b-company" maxlength="120"></div>
      ${
        (D().experiences ?? []).length
          ? `<div><label for="b-exp">${esc(T('h.y.experience'))}</label><select id="b-exp"><option value="">${esc(T('h.y.no_experience'))}</option>${(D().experiences as any[])
              .map((x) => `<option value="${x.id}">${esc(x.titles?.[getLocale()] || x.titles?.en || Object.values(x.titles)[0] || '')}</option>`)
              .join('')}</select></div>`
          : ''
      }
    </div>
    <label style="margin-top:8px;display:flex;gap:8px;align-items:center"><input type="checkbox" id="b-confirm" style="width:auto" checked> ${esc(T('h.y.send_confirmation'))}</label>
    <div class="grid2" style="margin-top:10px">
      <div><label for="b-req">${esc(T('h.book.request'))}</label><input id="b-req"></div>
      <div><label for="b-ref">${esc(T('h.x.referral'))}</label><input id="b-ref" maxlength="120" placeholder="${esc(T('h.x.referral_ph'))}"></div>
      <div><label for="b-lang">${esc(T('h.book.language'))}</label><select id="b-lang">${LOCALES.filter((l) => ((D().restaurant.locales as string[]) ?? [...LOCALES]).includes(l))
        .map((l) => `<option value="${l}" ${l === (D().restaurant.defaultLocale ?? getLocale()) ? 'selected' : ''}>${esc(LOCALE_NAMES[l])}</option>`).join('')}</select></div>
    </div>
    ${can('reservations.manage') ? `<label style="margin-top:8px;display:flex;gap:8px;align-items:center"><input type="checkbox" id="b-card" style="width:auto"> ${esc(T('x.card.take'))}</label>
      <p class="muted small" style="margin:2px 0 0">${esc(T('x.card.hint'))}</p>` : ''}
    <div id="b-card-box"></div>
    <div id="b-msg"></div>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button><button class="primary" data-act="b-submit">${esc(T('h.book.submit'))}</button></div>`,
    (root) => {
      const q = (id: string) => root.querySelector(id) as HTMLInputElement;
      /** The passcode travels as a header only while "Override pacing" is ticked. */
      const hdr = (): Record<string, string> | undefined => {
        const on = (q('#b-over') as HTMLInputElement).checked;
        const code = q('#b-pass')?.value.trim();
        return on && code ? { 'x-passcode': code } : undefined;
      };
      root.addEventListener('change', (ev) => {
        if ((ev.target as HTMLElement).id === 'b-over') {
          const pass = q('#b-pass');
          if (pass) pass.style.display = (ev.target as HTMLInputElement).checked ? '' : 'none';
        }
      });
      // Guest book search: pick an existing guest, or just type a new one below.
      let findTimer = 0 as any;
      root.addEventListener('input', (ev) => {
        const el = ev.target as HTMLInputElement;
        if (el.id === 'b-search') {
          clearTimeout(findTimer);
          findTimer = setTimeout(async () => {
            const box = root.querySelector('#b-guests')!;
            const term = el.value.trim();
            if (term.length < 2) {
              box.innerHTML = '';
              return;
            }
            try {
              const r = await api<any>('GET', `/v1/restaurants/${S.rid}/guests/lookup?q=${encodeURIComponent(term)}`);
              box.innerHTML = r.guests.length
                ? r.guests
                    .map(
                      (g: any) => `<button type="button" data-act="b-guest" data-g='${esc(JSON.stringify(g))}'>
                        <strong>${esc(g.name)}</strong> <span class="muted small">${esc(g.phone ?? g.email ?? '')}</span>
                        <span class="small">${g.visits} ${esc(T('h.x.visits'))}${g.noShows ? ` · ${g.noShows} ${esc(T('h.x.noshows'))}` : ''}</span></button>`,
                    )
                    .join('')
                : `<span class="muted small">${esc(T('h.x.no_guest'))}</span>`;
            } catch {
              box.innerHTML = '';
            }
          }, 250);
        } else if (['b-first', 'b-last', 'b-phone', 'b-email'].includes(el.id) && pickedGuest) {
          // Editing the name or contact details means this is not that guest any more.
          pickedGuest = null;
          root.querySelector('#b-picked')!.innerHTML = '';
        }
      });
      let cardHold: { lockId: string; payment: any; key: string } | null = null;
      let stripeJs: any = null;
      let cardEl: any = null;
      const showCardBox = async (pay: any) => {
        const box = root.querySelector('#b-card-box')!;
        const amount = new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: pay.currency }).format(pay.amountMinor / 100);
        const note = pay.kind === 'deposit' ? T('x.card.deposit_note', { amount }) : T('x.card.hold_note');
        box.innerHTML = `<fieldset><legend>${esc(T('x.card.title'))}</legend><p class="small" style="margin:0 0 6px">${esc(note)}</p>${
          pay.provider === 'mock'
            ? `<p class="muted small">${esc(T('x.card.test'))}</p>`
            : `<div id="b-card-el" style="padding:10px;border:1px solid var(--line);border-radius:8px;background:#fff"></div>`}</fieldset>`;
        if (pay.provider !== 'mock') {
          const w = window as any;
          if (!w.Stripe)
            await new Promise<void>((ok, bad) => {
              const sc = document.createElement('script');
              sc.src = 'https://js.stripe.com/v3/';
              sc.onload = () => ok();
              sc.onerror = () => bad(new Error(T('x.card.not_ready')));
              document.head.appendChild(sc);
            });
          stripeJs = w.Stripe(pay.publishableKey);
          cardEl = stripeJs.elements({ locale: getLocale() }).create('card', { hidePostalCode: true });
          cardEl.mount('#b-card-el');
        }
      };
      const completeCard = async (pay: any, name: string, email: string) => {
        if (pay.provider === 'mock') {
          await api('POST', '/v1/dev/mock-payments/complete', { clientSecret: pay.clientSecret });
          return;
        }
        if (!stripeJs || !cardEl) throw new Error(T('x.card.not_ready'));
        const args = { payment_method: { card: cardEl, billing_details: { name, email: email || undefined } } };
        const res = pay.kind === 'card_hold' ? await stripeJs.confirmCardSetup(pay.clientSecret, args) : await stripeJs.confirmCardPayment(pay.clientSecret, args);
        if (res.error) throw new Error(res.error.message ?? T('x.card.failed'));
      };
      const find = async () => {
        const box = root.querySelector('#b-times')!;
        box.innerHTML = `<span class="muted">${esc(T('h.book.searching'))}</span>`;
        try {
          const r = await api<any>('POST', `/v1/restaurants/${S.rid}/host/availability`, {
            date: q('#b-date').value,
            time: q('#b-time').value,
            partySize: Number(q('#b-party').value),
            windowMinutes: 120,
            daysForward: 0,
            ignorePacing: (q('#b-over') as HTMLInputElement).checked,
            experienceId: (root.querySelector('#b-exp') as HTMLSelectElement | null)?.value || undefined,
          }, hdr());
          const slots = r.days[0].slots.filter((s: any) => s.available);
          chosen = q('#b-time').value;
          box.innerHTML = slots.length
            ? slots
                .map((s: any) => `<button data-act="b-pick" data-t="${s.localTime}" aria-pressed="${s.localTime === chosen}">${s.localTime}${s.policy && s.policy.kind !== 'none' ? ' 💳' : ''}${s.load ? `<small class="load ${s.load.maxCovers != null && s.load.covers >= s.load.maxCovers * 0.8 ? 'high' : ''}">${s.load.covers}${s.load.maxCovers != null ? '/' + s.load.maxCovers : ''}</small>` : ''}</button>`)
                .join('')
            : `<span class="muted">${esc(T('h.book.none'))}${r.days[0].noTimesReasons[0] ? ' (' + esc(T(`reason.${r.days[0].noTimesReasons[0]}`)) + ')' : ''}.</span>
               ${can('waitlist.manage') ? `<button data-act="b-waitlist">${esc(T('h.y.to_waitlist'))}</button>` : ''}`;
        } catch (e) {
          box.innerHTML = `<span class="over">${esc(errMsg(e))}</span>`;
        }
      };
      root.addEventListener('click', async (ev) => {
        const el = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
        if (!el) return;
        if (el.dataset.act === 'b-find') void find();
        if (el.dataset.act === 'b-waitlist') {
          const n = Number(q('#b-party').value);
          closeModal();
          waitlistModal({ party: n });
        }
        if (el.dataset.act === 'b-guest') {
          const g = JSON.parse(el.dataset.g!);
          pickedGuest = g.id;
          q('#b-first').value = g.firstName;
          q('#b-last').value = g.lastName;
          q('#b-phone').value = g.phone ?? '';
          q('#b-email').value = g.email ?? '';
          root.querySelector('#b-guests')!.innerHTML = '';
          root.querySelector('#b-picked')!.innerHTML = `<div class="counters"><span><strong>${g.upcoming}</strong>${esc(T('h.x.upcoming'))}</span><span><strong>${g.visits}</strong>${esc(T('h.x.visits'))}</span>
            <span><strong>${g.cancellations}</strong>${esc(T('h.x.cancellations'))}</span><span><strong>${g.noShows}</strong>${esc(T('h.x.noshows'))}</span></div>
            ${(g.tags as string[]).map((t) => `<span class="chip">${esc(t)}</span>`).join(' ')}`;
        }
        if (el.dataset.act === 'b-pick') {
          chosen = el.dataset.t!;
          q('#b-time').value = chosen;
          root.querySelectorAll('#b-times button').forEach((b) => b.setAttribute('aria-pressed', String((b as HTMLElement).dataset.t === chosen)));
        }
        if (el.dataset.act === 'b-submit') {
          const msg = root.querySelector('#b-msg')!;
          msg.innerHTML = '';
          try {
            const wantCard = !!(root.querySelector('#b-card') as HTMLInputElement | null)?.checked;
            const when = {
              date: q('#b-date').value,
              time: q('#b-time').value,
              partySize: Number(q('#b-party').value),
              overridePacing: (q('#b-over') as HTMLInputElement).checked,
              experienceId: (root.querySelector('#b-exp') as HTMLSelectElement | null)?.value || undefined,
            };
            // Phone card, step 1: hold the table and show the card field.
            if (wantCard && !cardHold) {
              const h = await api<any>('POST', `/v1/restaurants/${S.rid}/host/book/card`, { ...when, source: 'phone' }, hdr());
              cardHold = { lockId: h.lockId, payment: h.payment, key: JSON.stringify(when) };
              if (h.payment) {
                await showCardBox(h.payment);
                (el as HTMLButtonElement).textContent = T('x.card.go');
                return;
              }
              root.querySelector('#b-card-box')!.innerHTML = `<p class="muted small">${esc(T('x.card.none'))}</p>`;
            }
            if (cardHold && cardHold.key !== JSON.stringify(when)) {
              cardHold = null;
              root.querySelector('#b-card-box')!.innerHTML = '';
              (el as HTMLButtonElement).textContent = T('h.book.submit');
              throw new Error(T('x.card.changed'));
            }
            // Phone card, step 2: the guest's card goes to the provider, then the booking is confirmed.
            let paymentToken: string | undefined;
            if (cardHold?.payment) {
              await completeCard(cardHold.payment, `${q('#b-first').value} ${q('#b-last').value}`.trim(), q('#b-email').value.trim());
              paymentToken = cardHold.payment.paymentId;
            }
            const r = await api<any>('POST', `/v1/restaurants/${S.rid}/host/book`, {
              ...(cardHold ? { lockId: cardHold.lockId, paymentToken } : {}),
              date: q('#b-date').value,
              time: q('#b-time').value,
              partySize: Number(q('#b-party').value),
              overridePacing: (q('#b-over') as HTMLInputElement).checked,
              guest: {
                firstName: q('#b-first').value.trim(),
                lastName: q('#b-last').value.trim(),
                phone: q('#b-phone').value.trim() || null,
                email: q('#b-email').value.trim() || null,
                company: q('#b-company').value.trim() || null,
              },
              experienceId: (root.querySelector('#b-exp') as HTMLSelectElement | null)?.value || null,
              sendConfirmation: (root.querySelector('#b-confirm') as HTMLInputElement).checked,
              specialRequest: q('#b-req').value.trim() || null,
              referral: q('#b-ref').value.trim() || null,
              guestId: pickedGuest,
              locale: q('#b-lang').value,
              idempotencyKey: crypto.randomUUID(),
            }, hdr());
            // Booked from a table on the timeline: keep that table for the party.
            if (prefill.tableId) {
              try {
                await api('POST', `/v1/host/reservations/${r.id}/assign`, { tableIds: [prefill.tableId] });
              } catch {
                /* the booking stands; the host can assign a table by hand */
              }
            }
            closeModal();
            toast(T('h.book.done', { name: r.guest.name, code: r.code }));
            if (r.startsAt && localDate(r.startsAt) === S.date) S.sel = { kind: 'party', id: r.id };
            await load();
          } catch (e) {
            msg.innerHTML = `<div class="warn-box">${esc(errMsg(e))}</div>`;
          }
        }
      });
    },
  );
}

function localDate(iso: string): string {
  return dateIn(iso, S.tz);
}

/** Day list and table cards: choose what to print, then open the PDF (print it from the viewer). */
function printModal() {
  const d = D();
  openModal(
    `<h2>${esc(T('pr.title'))}</h2>
    <p class="muted small">${esc(T('pr.hint'))}</p>
    <div class="grid2">
      <div><label for="pr-date">${esc(T('pr.date'))}</label><input id="pr-date" type="date" value="${esc(S.date)}"></div>
      <div><label for="pr-shift">${esc(T('pr.shift'))}</label><select id="pr-shift"><option value="">${esc(T('pr.all_shifts'))}</option>${(d.shifts as any[])
        .map((s) => `<option value="${esc(s.id)}">${esc(s.name)}</option>`).join('')}</select></div>
    </div>
    <fieldset style="margin-top:10px;border:0;padding:0">
      <label class="cfg-check" style="display:flex;gap:8px"><input type="radio" name="pr-mode" value="list" checked style="width:auto"> <span><strong>${esc(T('pr.list'))}</strong><br><span class="muted small">${esc(T('pr.list_hint'))}</span></span></label>
      <label class="cfg-check" style="display:flex;gap:8px;margin-top:8px"><input type="radio" name="pr-mode" value="cards" style="width:auto"> <span><strong>${esc(T('pr.cards'))}</strong><br><span class="muted small">${esc(T('pr.cards_hint'))}</span></span></label>
      <label class="cfg-check" style="display:flex;gap:8px;margin-top:8px"><input type="radio" name="pr-mode" value="cards-all" style="width:auto"> <span><strong>${esc(T('pr.cards_all'))}</strong><br><span class="muted small">${esc(T('pr.cards_all_hint'))}</span></span></label>
    </fieldset>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button><button class="primary" data-act="pr-open">${esc(T('pr.open'))}</button></div>`,
    (root) => {
      root.addEventListener('click', (ev) => {
        if (!(ev.target as HTMLElement).closest('[data-act="pr-open"]')) return;
        const v = (id: string) => (root.querySelector(id) as HTMLInputElement).value;
        const mode = (root.querySelector('input[name="pr-mode"]:checked') as HTMLInputElement).value;
        const shift = v('#pr-shift');
        window.open(`/v1/restaurants/${S.rid}/print/day.pdf?date=${encodeURIComponent(v('#pr-date'))}&mode=${mode}${shift ? `&shiftId=${shift}` : ''}`, '_blank', 'noopener');
      });
    },
  );
}

function waitlistModal(prefill: { party?: number; minutes?: number } = {}) {
  openModal(
    `<h2>${esc(T('h.wl.title'))}</h2>
    <div class="grid2">
      <div><label for="w-name">${esc(T('h.wl.name'))}</label><input id="w-name" placeholder="${esc(T('h.wl.name_ph'))}"></div>
      <div><label for="w-party">${esc(T('h.wl.party'))}</label><input id="w-party" type="number" min="1" max="40" value="${prefill.party ?? 2}"></div>
      <div><label for="w-quote">${esc(T('h.wl.quote'))}</label><input id="w-quote" type="number" min="0" max="300" value="${prefill.minutes ?? ''}"></div>
      <div><label for="w-phone">${esc(T('h.wl.phone'))}</label><input id="w-phone" type="tel"></div>
    </div>
    <label style="margin-top:10px;display:flex;gap:8px;align-items:center"><input type="checkbox" id="w-sms" style="width:auto"> ${esc(T('h.wl.sms'))}</label>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.cancel'))}</button><button class="primary" data-act="w-submit">${esc(T('h.wl.submit'))}</button></div>`,
    (root) => {
      root.addEventListener('click', async (ev) => {
        const el = (ev.target as HTMLElement).closest('[data-act="w-submit"]');
        if (!el) return;
        const g = (id: string) => (root.querySelector(id) as HTMLInputElement).value.trim();
        const body: any = {
          guestName: g('#w-name') || T('common.guest'),
          partySize: Number(g('#w-party')),
          phone: g('#w-phone') || null,
          smsOptIn: (root.querySelector('#w-sms') as HTMLInputElement).checked,
        };
        if (g('#w-quote')) body.quotedMinutes = Number(g('#w-quote'));
        try {
          const r = await api<any>('POST', `/v1/restaurants/${S.rid}/waitlist`, body);
          closeModal();
          toast(T(r.tableFreeNow ? 'h.wl.quoted_free' : 'h.wl.quoted', { n: r.quotedMinutes }));
          await load();
        } catch (e) {
          toast(errMsg(e), true);
        }
      });
    },
  );
}

function editModal(id: string) {
  const p = party(id);
  if (!p) return;
  const local = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: S.tz }).format(new Date(p.startsAt));
  openModal(
    `<h2>${esc(T('h.edit.title', { name: p.name }))}</h2>
    <div class="grid3">
      <div><label for="e-date">${esc(T('h.book.date'))}</label><input id="e-date" type="date" value="${localDate(p.startsAt)}"></div>
      <div><label for="e-time">${esc(T('h.book.time'))}</label><select id="e-time">${timeOptions(local)}</select></div>
      <div><label for="e-party">${esc(T('h.book.party'))}</label><input id="e-party" type="number" min="1" max="60" value="${p.partySize}"></div>
    </div>
    <div class="grid2" style="margin-top:10px">
      <div><label for="e-dur">${esc(T('h.edit.duration'))}</label><input id="e-dur" type="number" min="15" max="600" step="5" value="${p.durationMinutes}"></div>
      <div><label for="e-req">${esc(T('h.book.request'))}</label><input id="e-req" value="${esc(p.specialRequest ?? '')}"></div>
    </div>
    <div class="row-end" style="justify-content:flex-start"><button data-act="e-find">${esc(T('h.y.find_times'))}</button></div>
    <div id="e-times" class="times"></div>
    <label style="margin-top:10px;display:flex;gap:8px;align-items:center"><input type="checkbox" id="e-over" style="width:auto"> ${esc(T('h.edit.override'))}</label>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.cancel'))}</button><button class="primary" data-act="e-submit">${esc(T('common.save'))}</button></div>`,
    (root) => {
      root.addEventListener('click', async (ev) => {
        const pick = (ev.target as HTMLElement).closest('[data-act="e-pick"]') as HTMLElement | null;
        if (pick) {
          (root.querySelector('#e-time') as HTMLSelectElement).value = pick.dataset.t!;
          root.querySelectorAll('#e-times button').forEach((b) => b.setAttribute('aria-pressed', String(b === pick)));
          return;
        }
        if ((ev.target as HTMLElement).closest('[data-act="e-find"]')) {
          const box = root.querySelector('#e-times')!;
          box.innerHTML = `<span class="muted">${esc(T('h.book.searching'))}</span>`;
          try {
            const r = await api<any>('POST', `/v1/restaurants/${S.rid}/host/availability`, {
              date: (root.querySelector('#e-date') as HTMLInputElement).value,
              time: (root.querySelector('#e-time') as HTMLSelectElement).value,
              partySize: Number((root.querySelector('#e-party') as HTMLInputElement).value),
              windowMinutes: 180,
              daysForward: 0,
            });
            const slots = r.days[0].slots.filter((x: any) => x.available);
            box.innerHTML = slots.length
              ? slots.map((x: any) => `<button data-act="e-pick" data-t="${x.localTime}">${x.localTime}${x.load ? `<small class="load">${x.load.covers}${x.load.maxCovers != null ? '/' + x.load.maxCovers : ''}</small>` : ''}</button>`).join('')
              : `<span class="muted">${esc(T('h.book.none'))}</span>`;
          } catch (e) {
            box.innerHTML = `<span class="over">${esc(errMsg(e))}</span>`;
          }
          return;
        }
        if (!(ev.target as HTMLElement).closest('[data-act="e-submit"]')) return;
        const g = (x: string) => root.querySelector(x) as HTMLInputElement;
        try {
          const moved = g('#e-date').value !== localDate(p.startsAt) || g('#e-time').value !== local || Number(g('#e-party').value) !== p.partySize;
          if (moved)
            await api('POST', `/v1/host/reservations/${id}/move`, {
              date: g('#e-date').value,
              time: g('#e-time').value,
              partySize: Number(g('#e-party').value),
              overridePacing: g('#e-over').checked,
            });
          const dur = Number(g('#e-dur').value);
          await api('PATCH', `/v1/host/reservations/${id}`, {
            durationMinutes: dur !== p.durationMinutes ? dur : undefined,
            specialRequest: g('#e-req').value.trim() || null,
          });
          closeModal();
          toast(T('h.edit.saved'));
          await load();
        } catch (e) {
          toast(errMsg(e), true);
        }
      });
    },
  );
}

function statusesModal() {
  const rows = D()
    .statuses.filter((s: any) => s.phase === 'pre' || s.phase === 'seated')
    .map(
      (s: any) => `<label style="display:flex;gap:8px;align-items:center;color:var(--ink);font-size:14px;margin:4px 0">
        <input type="checkbox" style="width:auto" data-code="${s.code}" ${s.enabled ? 'checked' : ''} ${s.locked ? 'disabled' : ''}>
        <span style="width:10px;height:10px;border-radius:50%;background:${s.color}"></span>${esc(statusLabel(s.code))}${s.locked ? ` <span class="muted small">${esc(T('h.st.locked'))}</span>` : ''}</label>`,
    )
    .join('');
  openModal(`<h2>${esc(T('h.st.title'))}</h2><p class="muted small">${esc(T('h.st.hint'))}</p>${rows}<div class="row-end"><button class="primary" data-act="close-modal">${esc(T('common.done'))}</button></div>`, (root) => {
    root.addEventListener('change', async (ev) => {
      const i = ev.target as HTMLInputElement;
      if (!i.dataset.code) return;
      try {
        await api('PUT', `/v1/restaurants/${S.rid}/statuses/${i.dataset.code}`, { enabled: i.checked });
        await load();
      } catch (e) {
        i.checked = !i.checked;
        toast(errMsg(e), true);
      }
    });
  });
}

async function teamModal() {
  let data: any;
  try {
    data = await api('GET', `/v1/restaurants/${S.rid}/staff`);
  } catch (e) {
    toast(errMsg(e), true);
    return;
  }
  const mine = new Set(S.user!.permissions);
  const roleOptions = (sel: string) =>
    data.roles.map((r: any) => `<option value="${r.id}" ${r.id === sel ? 'selected' : ''}>${esc(roleLabel(r.name))}</option>`).join('');
  const userRows = data.users
    .map(
      (u: any) => `<tr>
        <td><strong>${esc(u.name)}</strong><br><span class="muted small">${esc(u.email)}</span></td>
        <td><select data-team="role" data-id="${u.id}" aria-label="${esc(T('h.team.role_for', { name: u.name }))}">${roleOptions(u.roleId)}</select></td>
        <td><label style="margin:0;display:flex;gap:6px;align-items:center;color:var(--ink)"><input type="checkbox" style="width:auto" data-team="active" data-id="${u.id}" ${u.active ? 'checked' : ''}> ${esc(T('h.team.active'))}</label></td>
        <td><button data-team="reset" data-id="${u.id}">${esc(T('h.team.reset_pw'))}</button>
            ${u.mfaEnabled ? `<button data-team="mfa-reset" data-id="${u.id}" title="${esc(T('h.team.mfa_on'))}">${esc(T('h.team.reset_mfa'))}</button>` : ''}</td>
      </tr>`,
    )
    .join('');
  const roleCards = data.roles
    .map(
      (r: any) => `<div class="card" style="margin:6px 0;padding:10px">
        <strong>${esc(roleLabel(r.name))}</strong> <span class="muted small">${esc(r.isSystem ? T('h.team.builtin') : T('h.team.custom'))} · ${esc(TN('h.team.members', r.members))}</span>
        ${r.isSystem ? '' : `<button class="danger" style="float:right" data-team="del-role" data-id="${r.id}">${esc(T('common.delete'))}</button>`}
        <div style="margin-top:6px">${r.permissions.map((p: string) => `<span class="chip" title="${esc(permLabel(p, p))}">${esc(p)}</span>`).join('') || `<span class="muted small">${esc(T('h.team.no_perms'))}</span>`}</div>
      </div>`,
    )
    .join('');
  const permBoxes = data.permissions
    .map(
      (p: any) => `<label style="display:flex;gap:8px;align-items:flex-start;color:var(--ink);font-size:13px;margin:3px 0">
        <input type="checkbox" style="width:auto;margin-top:3px" data-perm="${p.code}" ${mine.has(p.code) ? '' : 'disabled'}>
        <span><code>${esc(p.code)}</code><br><span class="muted small">${esc(permLabel(p.code, p.description))}</span></span></label>`,
    )
    .join('');

  const sec = data.security ?? { mfaRequired: false, sso: null };
  let redirectUri = '';
  try {
    redirectUri = (await api<any>('GET', `/v1/restaurants/${S.rid}/sso`)).redirectUri ?? '';
  } catch {
    /* shown without it */
  }
  const sso = sec.sso;
  const securityHtml = `
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.team.policy'))}</h3>
    <label style="display:flex;gap:8px;align-items:center;color:var(--ink);font-size:14px">
      <input type="checkbox" style="width:auto" data-team="mfa-required" ${sec.mfaRequired ? 'checked' : ''}>
      ${esc(T('h.team.require_mfa'))}</label>
    <details style="margin-top:10px" ${sso ? 'open' : ''}><summary>${esc(T('h.team.sso'))}</summary>
      <p class="muted small" style="margin:8px 0">${esc(T('h.team.sso_hint', { uri: '\u0000' })).split('\u0000').join(`<code>${esc(redirectUri)}</code>`)}</p>
      <div class="grid2">
        <div><label for="s-issuer">${esc(T('h.team.issuer'))}</label><input id="s-issuer" value="${esc(sso?.issuer ?? '')}" placeholder="https://accounts.example.com"></div>
        <div><label for="s-client">${esc(T('h.team.client_id'))}</label><input id="s-client" value="${esc(sso?.clientId ?? '')}"></div>
        <div><label for="s-secret">${esc(T('h.team.client_secret'))} ${sso?.hasSecret ? esc(T('h.team.keep_blank')) : ''}</label><input id="s-secret" type="password" autocomplete="off"></div>
        <div><label for="s-domains">${esc(T('h.team.domains'))}</label><input id="s-domains" value="${esc((sso?.allowedDomains ?? []).join(', '))}"></div>
      </div>
      <label style="display:flex;gap:8px;align-items:center;color:var(--ink);font-size:14px;margin-top:8px"><input id="s-enabled" type="checkbox" style="width:auto" ${sso?.enabled !== false ? 'checked' : ''}> ${esc(T('h.team.enabled'))}</label>
      <label style="display:flex;gap:8px;align-items:center;color:var(--ink);font-size:14px"><input id="s-enforce" type="checkbox" style="width:auto" ${sso?.enforce ? 'checked' : ''}> ${esc(T('h.team.enforce'))}</label>
      <div class="row-end"><button class="primary" data-team="sso-save">${esc(T('h.team.sso_save'))}</button>${sso ? `<button class="danger" data-team="sso-delete">${esc(T('common.remove'))}</button>` : ''}</div>
    </details>`;

  openModal(
    `<h2>${esc(T('h.team.title'))}</h2>
    <h3 style="margin:8px 0;font-size:13px" class="muted">${esc(T('h.team.staff'))}</h3>
    <table style="width:100%;border-collapse:collapse"><tbody>${userRows}</tbody></table>
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.team.add_person'))}</h3>
    <div class="grid2">
      <div><label for="t-name">${esc(T('h.team.name'))}</label><input id="t-name"></div>
      <div><label for="t-email">${esc(T('h.team.email'))}</label><input id="t-email" type="email"></div>
      <div><label for="t-role">${esc(T('h.team.role'))}</label><select id="t-role">${roleOptions(data.roles.find((r: any) => r.name === 'Front of house')?.id ?? '')}</select></div>
      <div><label for="t-pass">${esc(T('h.team.initial_pw'))}</label><input id="t-pass" type="password" autocomplete="new-password"></div>
    </div>
    <div class="row-end" style="margin-top:8px"><button class="primary" data-team="add-user">${esc(T('h.team.add_btn'))}</button></div>
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.team.roles'))}</h3>
    ${roleCards}
    <details style="margin-top:8px"><summary>${esc(T('h.team.new_role'))}</summary>
      <div style="margin-top:8px"><label for="r-name">${esc(T('h.team.role_name'))}</label><input id="r-name"></div>
      <div style="margin-top:8px">${permBoxes}</div>
      <div class="row-end"><button data-team="add-role">${esc(T('h.team.create_role'))}</button></div>
    </details>
    <h3 style="margin:16px 0 6px;font-size:13px" class="muted">${esc(T('h.team.passcode'))}</h3>
    <p class="muted small">${esc(data.passcodeConfigured ? T('h.team.pass_set') : T('h.team.pass_unset'))} ${esc(T('h.team.pass_hint'))}</p>
    <div class="grid2"><div><input id="p-code" type="password" inputmode="numeric" maxlength="8" placeholder="${esc(T('h.team.pass_ph'))}" aria-label="${esc(T('h.team.pass_new'))}"></div>
      <div><button data-team="set-pass">${esc(T('h.team.set_pass'))}</button> ${data.passcodeConfigured ? `<button class="danger" data-team="clear-pass">${esc(T('common.remove'))}</button>` : ''}</div></div>
    ${securityHtml}
    <div id="team-msg"></div>
    <div class="row-end"><button class="primary" data-act="close-modal">${esc(T('common.done'))}</button></div>`,
    (root) => {
      const say = (m: string, bad = false) => {
        root.querySelector('#team-msg')!.innerHTML = `<div class="${bad ? 'warn-box' : 'muted small'}" role="${bad ? 'alert' : 'status'}">${esc(m)}</div>`;
      };
      const redo = async () => {
        closeModal();
        await teamModal();
      };
      const g = (id: string) => (root.querySelector(id) as HTMLInputElement).value.trim();
      root.addEventListener('change', async (ev) => {
        const el = ev.target as HTMLInputElement;
        const k = el.dataset.team;
        if (!k) return;
        try {
          if (k === 'role') await api('PATCH', `/v1/staff/${el.dataset.id}`, { roleId: (el as unknown as HTMLSelectElement).value });
          if (k === 'active') await api('PATCH', `/v1/staff/${el.dataset.id}`, { active: el.checked });
          if (k === 'mfa-required') await api('PUT', `/v1/restaurants/${S.rid}/security`, { mfaRequired: el.checked });
          say(T('h.team.saved'));
        } catch (e) {
          say(errMsg(e), true);
          await redo();
        }
      });
      root.addEventListener('click', async (ev) => {
        const el = (ev.target as HTMLElement).closest('[data-team]') as HTMLElement | null;
        if (!el) return;
        const k = el.dataset.team;
        try {
          if (k === 'add-user') {
            await api('POST', `/v1/restaurants/${S.rid}/staff`, {
              name: g('#t-name'),
              email: g('#t-email'),
              roleId: (root.querySelector('#t-role') as HTMLSelectElement).value,
              password: (root.querySelector('#t-pass') as HTMLInputElement).value,
            });
            await redo();
          } else if (k === 'reset') {
            const pw = window.prompt(T('h.team.pw_prompt'));
            if (pw) {
              await api('POST', `/v1/staff/${el.dataset.id}/password`, { password: pw });
              say(T('h.team.pw_reset_done'));
            }
          } else if (k === 'add-role') {
            const perms = Array.from(root.querySelectorAll('[data-perm]'))
              .filter((i) => (i as HTMLInputElement).checked)
              .map((i) => (i as HTMLElement).dataset.perm!);
            await api('POST', `/v1/restaurants/${S.rid}/roles`, { name: g('#r-name'), permissions: perms });
            await redo();
          } else if (k === 'del-role') {
            await api('DELETE', `/v1/roles/${el.dataset.id}`);
            await redo();
          } else if (k === 'set-pass') {
            await api('PUT', `/v1/restaurants/${S.rid}/passcode`, { passcode: g('#p-code') });
            await redo();
          } else if (k === 'clear-pass') {
            await api('PUT', `/v1/restaurants/${S.rid}/passcode`, { passcode: null });
            await redo();
          } else if (k === 'mfa-reset') {
            if (window.confirm(T('h.team.mfa_reset_confirm'))) {
              await api('POST', `/v1/staff/${el.dataset.id}/mfa/reset`, {});
              await redo();
            }
          } else if (k === 'sso-save') {
            const checked = (id: string) => (root.querySelector(id) as HTMLInputElement).checked;
            const secret = (root.querySelector('#s-secret') as HTMLInputElement).value;
            await api('PUT', `/v1/restaurants/${S.rid}/sso`, {
              issuer: g('#s-issuer'),
              clientId: g('#s-client'),
              clientSecret: secret || undefined,
              allowedDomains: g('#s-domains').split(',').map((d) => d.trim()).filter(Boolean),
              enabled: checked('#s-enabled'),
              enforce: checked('#s-enforce'),
            });
            say(T('h.team.sso_saved'));
          } else if (k === 'sso-delete') {
            if (window.confirm(T('h.team.sso_remove_confirm'))) {
              await api('DELETE', `/v1/restaurants/${S.rid}/sso`);
              await redo();
            }
          }
        } catch (e) {
          say(errMsg(e), true);
        }
      });
    },
  );
}

/** The restaurant's own subscription invoices, with PDF downloads. */
async function invoicesModal() {
  let r: any;
  try {
    r = await api('GET', `/v1/restaurants/${S.rid}/billing/invoices`);
  } catch (e) {
    toast(errMsg(e), true);
    return;
  }
  const money = (minor: number, cur: string) =>
    new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: cur }).format(minor / 100);
  const day = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`)) : '';
  const label = (s: string) => {
    const k = `h.inv.status.${s}`;
    return T(k) === k ? s : T(k);
  };
  const rows = r.invoices
    .map(
      (i: any) => `<tr><td><strong>${esc(i.number)}</strong>${i.kind === 'credit_note' ? `<br><span class="muted small">${esc(T('h.inv.credit_note'))}</span>` : ''}</td>
        <td>${esc(i.period)}</td><td>${esc(day(i.issuedOn))}</td><td>${esc(i.kind === 'credit_note' ? '' : day(i.dueOn))}</td>
        <td style="text-align:right">${esc(money(i.totalMinor, i.currency))}</td><td>${esc(label(i.status))}</td>
        <td><a href="/v1/restaurants/${esc(S.rid)}/billing/invoices/${esc(i.id)}/pdf" target="_blank" rel="noopener">${esc(T('h.inv.pdf'))}</a> · <a href="/v1/restaurants/${esc(S.rid)}/billing/invoices/${esc(i.id)}/zugferd.pdf" title="${esc(T('o.i.zugferd_title'))}">ZUGFeRD</a> · <a href="/v1/restaurants/${esc(S.rid)}/billing/invoices/${esc(i.id)}/xrechnung.xml" title="${esc(T('o.i.xr_title'))}">XRechnung</a></td></tr>`,
    )
    .join('');
  openModal(`<div style="min-width:min(720px,90vw)">
    <div style="display:flex;justify-content:space-between;gap:8px"><h2>${esc(T('h.inv.title'))}</h2>
      <button class="ghost" data-act="close-modal" aria-label="${esc(T('common.close'))}">✕</button></div>
    <p class="muted small">${esc(T('h.inv.intro'))}</p>
    ${r.paymentMethod ? `<p class="small">${esc(T('h.inv.method', { method: r.paymentMethod }))}</p>` : ''}
    ${
      r.invoices.length
        ? `<table class="grid" style="width:100%"><thead><tr><th>${esc(T('h.inv.number'))}</th><th>${esc(T('h.inv.period'))}</th>
            <th>${esc(T('h.inv.date'))}</th><th>${esc(T('h.inv.due'))}</th><th style="text-align:right">${esc(T('h.inv.amount'))}</th>
            <th>${esc(T('h.inv.status'))}</th><th></th></tr></thead><tbody>${rows}</tbody></table>`
        : `<p class="muted">${esc(T('h.inv.empty'))}</p>`
    }
  </div>`);
}

/** Account security: two-step sign-in and password. `forced` blocks until enrolled. */
async function securityModal(forced = false) {
  let me: any;
  try {
    me = await api('GET', '/v1/auth/me');
  } catch (e) {
    toast(errMsg(e), true);
    return;
  }
  const state: { setup: null | { secret: string; otpauthUri: string }; codes: string[] | null; msg: string; bad: boolean } = {
    setup: null,
    codes: null,
    msg: '',
    bad: false,
  };
  const group = (s: string) => s.replace(/(.{4})/g, '$1 ').trim();

  const draw = (root: HTMLElement | null) => {
    const m = me.mfa;
    const box = root ?? modalRoot;
    const body = state.codes
      ? `<div class="info-box"><strong>${esc(T('h.sec.save_codes'))}</strong> ${esc(T('h.sec.codes_hint'))}</div>
         <pre class="codes">${state.codes.map((c) => esc(c)).join('\n')}</pre>
         <div class="row-end"><button data-sec="copy">${esc(T('h.sec.copy'))}</button><button class="primary" data-sec="done">${esc(T('h.sec.saved_them'))}</button></div>`
      : state.setup
        ? `<ol class="small" style="padding-left:18px">
             <li>${esc(T('h.sec.step1'))}</li>
             <li>${esc(T('h.sec.step2', { email: '\u0000' })).split('\u0000').join(`<strong>${esc(me.user.email)}</strong>`)}</li>
           </ol>
           <p class="key" aria-label="${esc(T('h.sec.key_aria'))}">${esc(group(state.setup.secret))}</p>
           <p class="muted small">${esc(T('h.sec.key_type'))}</p>
           <label for="sec-code">${esc(T('h.sec.enter_code'))}</label>
           <input id="sec-code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" autofocus>
           <div class="row-end"><button data-sec="cancel-setup">${esc(T('common.cancel'))}</button><button class="primary" data-sec="enable">${esc(T('h.sec.turn_on'))}</button></div>`
        : m.enabled
          ? `<p>${esc(T('h.sec.is_on'))} ${esc(TN('h.sec.codes_left', m.recoveryCodesRemaining))}</p>
             <div class="grid2">
               <div><label for="sec-pw">${esc(T('h.sec.password'))}</label><input id="sec-pw" type="password" autocomplete="current-password"></div>
               <div><label for="sec-c">${esc(T('h.sec.current_code'))}</label><input id="sec-c" inputmode="numeric" maxlength="20"></div>
             </div>
             <div class="row-end">
               <button data-sec="regen">${esc(T('h.sec.new_codes'))}</button>
               ${m.required ? '' : `<button class="danger" data-sec="disable">${esc(T('h.sec.turn_off'))}</button>`}
             </div>
             ${m.required ? `<p class="muted small">${esc(T('h.sec.required'))}</p>` : ''}`
          : `<p>${esc(m.required ? T('h.sec.required') : T('h.sec.add_step'))}</p>
             <div class="row-end"><button class="primary" data-sec="start">${esc(T('h.sec.setup'))}</button></div>`;
    box.innerHTML = `<div class="scrim"><div class="modal" role="dialog" aria-modal="true" aria-labelledby="sec-title">
      <h2 id="sec-title">${esc(T('h.sec.title'))}</h2>
      <h3 class="muted" style="margin:6px 0;font-size:13px">${esc(T('h.sec.twostep'))}</h3>
      ${body}
      ${state.msg ? `<div class="${state.bad ? 'warn-box' : 'info-box'}" role="${state.bad ? 'alert' : 'status'}">${esc(state.msg)}</div>` : ''}
      ${
        forced
          ? ''
          : `<h3 class="muted" style="margin:16px 0 6px;font-size:13px">${esc(T('h.sec.pw_heading'))}</h3>
             <div class="row-end" style="justify-content:flex-start"><button data-sec="password">${esc(T('h.sec.change_pw'))}</button></div>
             <div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button></div>`
      }
    </div></div>`;
  };

  S.modalOpen = true;
  draw(null);

  modalRoot.onclick = async (ev) => {
    const el = (ev.target as HTMLElement).closest('[data-sec]') as HTMLElement | null;
    if (!el) return;
    const k = el.dataset.sec;
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? '';
    state.msg = '';
    state.bad = false;
    try {
      if (k === 'start') {
        state.setup = await api('POST', '/v1/auth/mfa/setup');
      } else if (k === 'cancel-setup') {
        state.setup = null;
      } else if (k === 'enable') {
        const r = await api<any>('POST', '/v1/auth/mfa/enable', { code: val('sec-code') });
        state.codes = r.recoveryCodes;
        state.setup = null;
        me = await api('GET', '/v1/auth/me');
      } else if (k === 'copy') {
        await navigator.clipboard?.writeText((state.codes ?? []).join('\n'));
        state.msg = T('h.sec.copied');
      } else if (k === 'done') {
        state.codes = null;
        if (forced) {
          closeModal();
          await startSession();
          return;
        }
      } else if (k === 'regen') {
        const r = await api<any>('POST', '/v1/auth/mfa/recovery-codes', { password: val('sec-pw'), code: val('sec-c') });
        state.codes = r.recoveryCodes;
        me = await api('GET', '/v1/auth/me');
      } else if (k === 'disable') {
        await api('POST', '/v1/auth/mfa/disable', { password: val('sec-pw'), code: val('sec-c') });
        me = await api('GET', '/v1/auth/me');
        state.msg = T('h.sec.is_off');
      } else if (k === 'password') {
        modalRoot.onclick = null;
        passwordModal();
        return;
      }
    } catch (e) {
      state.msg = errMsg(e);
      state.bad = true;
    }
    draw(null);
  };
}

function passwordModal() {
  openModal(
    `<h2>${esc(T('h.pw.title'))}</h2>
    <label for="pw-cur">${esc(T('h.pw.current'))}</label><input id="pw-cur" type="password" autocomplete="current-password">
    <label for="pw-new" style="margin-top:10px">${esc(T('h.pw.new'))}</label><input id="pw-new" type="password" autocomplete="new-password">
    <p class="muted small">${esc(T('h.pw.hint'))}</p>
    <div id="pw-msg"></div>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.cancel'))}</button><button class="primary" data-act="pw-submit">${esc(T('h.pw.title'))}</button></div>`,
    (root) => {
      root.addEventListener('click', async (ev) => {
        if (!(ev.target as HTMLElement).closest('[data-act="pw-submit"]')) return;
        const cur = (root.querySelector('#pw-cur') as HTMLInputElement).value;
        const next = (root.querySelector('#pw-new') as HTMLInputElement).value;
        try {
          await api('POST', '/v1/auth/password', { current: cur, next });
          closeModal();
          toast(T('h.pw.changed'));
        } catch (e) {
          root.querySelector('#pw-msg')!.innerHTML = `<div class="warn-box" role="alert">${esc(errMsg(e))}</div>`;
        }
      });
    },
  );
}

function walkinModal(tableId: string) {
  const t = table(tableId);
  openModal(
    `<h2>${esc(T('h.walkin.title', { label: t.label }))}</h2>
    <div class="grid2">
      <div><label for="k-party">${esc(T('h.walkin.party'))}</label><input id="k-party" type="number" min="1" max="${t.maxSeats}" value="${Math.min(2, t.maxSeats)}"></div>
      <div><label for="k-name">${esc(T('h.walkin.name'))}</label><input id="k-name"></div>
    </div>
    <div class="row-end"><button data-act="close-modal">${esc(T('common.cancel'))}</button><button class="primary" data-act="k-submit">${esc(T('h.walkin.submit'))}</button></div>`,
    (root) => {
      root.addEventListener('click', async (ev) => {
        if (!(ev.target as HTMLElement).closest('[data-act="k-submit"]')) return;
        const size = Number((root.querySelector('#k-party') as HTMLInputElement).value);
        const name = (root.querySelector('#k-name') as HTMLInputElement).value.trim();
        closeModal();
        await act(() => api('POST', `/v1/restaurants/${S.rid}/host/walk-ins`, { tableIds: [tableId], partySize: size, guestName: name || undefined }), T('h.toast.walkin'));
      });
    },
  );
}

/** Tap on an empty stretch of the timeline: show whether the table is free and which party sizes fit. */
function tableSlotModal(tbl: any, minutes: number) {
  const d = D();
  const when = hhmm(minutes);
  const start = minutes;
  const end = minutes + 90;
  const busy = (tbl.bookings as any[]).some((b) => {
    const s0 = minutesOnShownDate(b.startsAt);
    const e0 = minutesOnShownDate(b.endsAt);
    return s0 < end && e0 > start;
  });
  const cell = (d.pacing as any[]).find((p) => p.localTime === when);
  const covers = cell ? `${cell.covers}${cell.maxCovers != null ? `/${cell.maxCovers}` : ''}` : '0';
  const sizes = Array.from({ length: Math.min(12, tbl.maxSeats + 2) }, (_, i) => i + 1);
  const row = (n: number) => {
    const warn = n < tbl.minSeats ? T('h.x.below_min') : n > tbl.maxSeats ? T('h.x.exceeds_max') : '';
    return `<button class="size-row ${warn ? 'warn' : ''}" data-act="tl-size" data-n="${n}">
      <span>${esc(TN('common.guests', n))}</span><span class="small">${warn ? esc(warn) : ''}</span><span class="small muted">${esc(covers)}</span></button>`;
  };
  openModal(
    `<h2>${esc(T('h.table_n', { label: tbl.label }))} · ${esc(when)}</h2>
    <p class="${busy ? 'over' : ''}">${esc(busy ? T('h.x.table_busy', { time: when }) : T('h.x.table_free', { time: when }))}</p>
    <p class="small muted">${esc(T('h.x.covers_at', { covers }))}</p>
    <div class="size-list">${sizes.map(row).join('')}</div>
    <div class="row-end">${can('tables.block') ? `<button data-act="b-block" data-id="${tbl.id}">${esc(T('h.t.block'))}</button>` : ''}<button data-act="close-modal">${esc(T('common.close'))}</button></div>`,
    (root) => {
      root.addEventListener('click', (ev) => {
        const b = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
        if (!b) return;
        if (b.dataset.act === 'tl-size') {
          closeModal();
          bookModal({ date: d.date, time: when, party: Number(b.dataset.n), tableId: tbl.id });
        } else if (b.dataset.act === 'b-block') {
          closeModal();
          S.sel = { kind: 'table', id: tbl.id };
          S.view = 'floor';
          render();
        }
      });
    },
  );
}

/** Notes for the day or shift, plus what the shift should know about at a glance. */
async function shiftNotesModal() {
  const d = D();
  const sum = d.summary ?? { largeParties: [], guestRequests: [], visitNotes: [], specialEvents: [] };
  const section = (title: string, rows: any[], withText: boolean) =>
    `<h4>${esc(title)} (${rows.length})</h4>${
      rows.length
        ? rows
            .map((r) => `<div class="note-row"><span class="small muted">${esc(tm(r.time))}</span> <strong>${esc(r.name)}</strong> ${r.partySize} ${withText && r.text ? `<br><span class="small">${esc(r.text)}</span>` : ''}</div>`)
            .join('')
        : ''
    }`;
  const notes = (d.shiftNotes as any[])
    .map(
      (n) => `<div class="card note-card"><div style="display:flex;justify-content:space-between;gap:8px"><strong>${esc(n.title)}</strong>
        <span class="small muted">${esc(n.shiftId ? (d.shifts.find((s: any) => s.id === n.shiftId)?.name ?? '') : T('h.x.all_day'))} · ${esc(T(`h.x.kind.${n.kind}`))}</span></div>
        ${n.body ? `<p class="small" style="margin:6px 0 0;white-space:pre-wrap">${esc(n.body)}</p>` : ''}
        ${can('host.seat') ? `<div class="row-end"><button class="danger" data-act="note-del" data-id="${n.id}">${esc(T('common.delete') === 'common.delete' ? T('h.x.delete') : T('common.delete'))}</button></div>` : ''}</div>`,
    )
    .join('');
  openModal(
    `<h2>${esc(T('h.x.notes'))} · ${esc(fmtDate(d.date))}</h2>
    ${can('host.seat') ? `<details open><summary><strong>${esc(T('h.x.add_note'))}</strong></summary>
      <div class="grid2"><div><label for="n-title">${esc(T('h.x.note_title'))}</label><input id="n-title" maxlength="100"></div>
      <div><label for="n-shift">${esc(T('h.x.note_scope'))}</label><select id="n-shift"><option value="">${esc(T('h.x.all_day'))}</option>${(d.shifts as any[])
        .map((s) => `<option value="${s.id}" ${s.id === d.shiftId ? 'selected' : ''}>${esc(s.name)}</option>`)
        .join('')}</select></div></div>
      <label for="n-body">${esc(T('h.x.note_body'))}</label><textarea id="n-body" rows="3" maxlength="1000"></textarea>
      <label class="small" style="display:flex;gap:6px;align-items:center"><input type="checkbox" id="n-event" style="width:auto"> ${esc(T('h.x.note_event'))}</label>
      <div class="row-end"><button class="primary" data-act="note-add">${esc(T('h.x.add_note'))}</button></div></details>` : ''}
    <div id="note-list">${notes || `<p class="muted">${esc(T('h.x.no_notes'))}</p>`}</div>
    <h3 class="muted" style="font-size:13px;margin-top:14px">${esc(T('h.x.shift_summary'))}</h3>
    ${section(T('h.x.sum_large'), sum.largeParties, false)}${section(T('h.x.sum_requests'), sum.guestRequests, true)}
    ${section(T('h.x.sum_visit'), sum.visitNotes, true)}${section(T('h.x.sum_events'), sum.specialEvents, true)}
    <div class="row-end"><button data-act="close-modal">${esc(T('common.close'))}</button></div>`,
    (root) => {
      root.addEventListener('click', async (ev) => {
        const b = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
        if (!b) return;
        try {
          if (b.dataset.act === 'note-add') {
            const title = (root.querySelector('#n-title') as HTMLInputElement).value.trim();
            if (!title) return;
            await api('POST', `/v1/restaurants/${S.rid}/shift-notes`, {
              day: d.date,
              title,
              body: (root.querySelector('#n-body') as HTMLTextAreaElement).value.trim(),
              shiftId: (root.querySelector('#n-shift') as HTMLSelectElement).value || null,
              kind: (root.querySelector('#n-event') as HTMLInputElement).checked ? 'event' : 'general',
            });
            closeModal();
            await load();
            void shiftNotesModal();
          } else if (b.dataset.act === 'note-del') {
            await api('DELETE', `/v1/restaurants/${S.rid}/shift-notes/${b.dataset.id}`);
            closeModal();
            await load();
            void shiftNotesModal();
          }
        } catch (e) {
          toast(errMsg(e), true);
        }
      });
    },
  );
}

// ------------------------------------------------------------------ events

/** "Table 24 is free now" or when it is free, before the host confirms a seating or assignment. */
function availabilityNote(t: any, mode: 'seat' | 'assign' | 'move' | 'waitseat'): { ok: boolean; text: string } {
  const busyNow = t.status !== 'free' && t.status !== 'held';
  if (t.blocked) return { ok: false, text: T('h.y.avail_blocked', { label: t.label }) };
  if (mode === 'assign') {
    return t.nextStartsAt
      ? { ok: true, text: T('h.y.avail_next', { label: t.label, time: tm(t.nextStartsAt) }) }
      : { ok: true, text: T('h.y.avail_free', { label: t.label }) };
  }
  if (busyNow) return { ok: false, text: T('h.y.avail_busy', { label: t.label }) };
  return t.nextStartsAt
    ? { ok: true, text: T('h.y.avail_until', { label: t.label, time: tm(t.nextStartsAt) }) }
    : { ok: true, text: T('h.y.avail_free', { label: t.label }) };
}

/** The servers who are next to receive a table: fewest guests seated now, longest since their last seating. */
function nextToSeatPanel(): string {
  const d = D();
  const servers: any[] = d.servers ?? [];
  if (!servers.length) return '';
  const seated = (d.parties.seated as any[]).filter((p) => p.seatedAt);
  const rows = servers
    .map((s) => {
      const mine = seated.filter((p) => p.tableIds.some((id: string) => (s.tableIds as string[]).includes(id)));
      const last = mine.map((p) => new Date(p.seatedAt).getTime()).sort((a, b) => b - a)[0];
      return { s, now: mine.length, covers: mine.reduce((n, p) => n + p.partySize, 0), ago: last ? Math.round((new Date(d.now).getTime() - last) / 60000) : null };
    })
    .sort((a, b) => a.now - b.now || (b.ago ?? 9999) - (a.ago ?? 9999));
  return `<div class="next-seat"><strong>${esc(T('h.y.next_to_seat'))}</strong>${rows
    .map(
      (r) => `<span class="ns"><i style="background:${r.s.color}">${esc(r.s.initials)}</i> ${esc(r.s.name)} · ${r.now}/${r.covers}${r.ago != null ? ` · ${esc(T('h.y.min_ago', { n: r.ago }))}` : ''}</span>`,
    )
    .join('')}</div>`;
}

async function onTableClick(id: string) {
  const m = S.mode;
  if (!m) {
    S.sel = { kind: 'table', id };
    render();
    return;
  }
  const t = table(id);
  if (t && ['seat', 'assign', 'move', 'waitseat'].includes(m.kind) && !S.confirmedTable) {
    const note = availabilityNote(t, m.kind as any);
    const proceed = await new Promise<boolean>((resolve) => {
      openModal(
        `<h2>${esc(T('h.y.confirm_table'))}</h2><p class="${note.ok ? '' : 'over'}">${esc(note.text)}</p>
        <div class="row-end"><button data-act="ct-no">${esc(T('common.cancel'))}</button><button class="primary" data-act="ct-yes">${esc(T('h.y.confirm'))}</button></div>`,
        (root) => {
          root.addEventListener('click', (ev) => {
            const b = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
            if (!b) return;
            resolve(b.dataset.act === 'ct-yes');
            closeModal();
          });
        },
      );
    });
    if (!proceed) return;
  }
  S.mode = null;
  if (m.kind === 'seat') await act(() => api('POST', `/v1/host/reservations/${m.partyId}/seat`, { tableIds: [id] }), T('h.toast.seated'));
  else if (m.kind === 'assign') await act(() => api('POST', `/v1/host/reservations/${m.partyId}/assign`, { tableIds: [id] }), T('h.toast.preassigned'));
  else if (m.kind === 'move') await act(() => api('POST', `/v1/host/reservations/${m.partyId}/assign`, { tableIds: [id] }), T('h.toast.moved'));
  else if (m.kind === 'waitseat') await act(() => api('POST', `/v1/waitlist/${m.waitId}/seat`, { tableIds: [id] }), T('h.toast.seated_wait'));
  render();
}

async function onDocumentClick(ev: MouseEvent) {
  const el = (ev.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
  if (!el || !S.data) return;
  const a = el.dataset.act!;
  const id = el.dataset.id ?? '';
  if (el.tagName === 'INPUT' || el.tagName === 'SELECT') return; // handled by change
  switch (a) {
    case 'scrim':
      if (ev.target === el) closeModal();
      return;
    case 'menu':
      S.menuOpen = !S.menuOpen;
      render();
      return;
    case 'menu-close':
      S.menuOpen = false;
      render();
      return;
    case 'nav': {
      S.menuOpen = false;
      render();
      const go = el.dataset.go!;
      const openConfig = (section: string) =>
        openConfigScreen({
          restaurantId: S.rid, canPublish: can('config.publish'), toast, section,
          onPublished: () => void load(), onClose: () => void load(),
        });
      const openExtras = (tab: string) =>
        openExtrasScreen({ restaurantId: S.rid, currency: D()?.restaurant?.currency ?? 'EUR', can: (p) => can(p as any), toast, tab, onClose: () => void load() });
      const routes: Record<string, () => void | Promise<void>> = {
        front: () => undefined,
        planning: () => openConfig('shifts'),
        floorplan: () => openConfig('layout'),
        profile: () => openConfig('branding'),
        settings: () => openConfig('settings'),
        policies: () => openConfig('policies'),
        widget: () => openConfig('widget'),
        private: () => openExtras('private'),
        menus: () => openExtras('menus'),
        marketing: () => openExtras('marketing'),
        alerts: () => openExtras('alerts'),
        integrations: () => openExtras('integrations'),
        reporting: () => openReportsScreen({ restaurantId: S.rid, toast, timezone: S.tz, onClose: () => undefined }),
        guests: () => openGuestsScreen({ restaurantId: S.rid, toast, canExport: can('guests.manage'), onClose: () => void load() }),
        notes: () => shiftNotesModal(),
        print: () => printModal(),
        accounts: () => teamModal(),
        billing: () => invoicesModal(),
        security: () => securityModal(false),
        log: () => auditModal(),
        signout: async () => {
          try {
            await api('POST', '/v1/auth/logout');
          } catch {
            /* cookie cleared server side */
          }
          loginView();
        },
      };
      await routes[go]?.();
      return;
    }
    case 'close-modal':
      closeModal();
      return;
    case 'date': {
      const n = Number(el.dataset.n);
      const prev = { date: S.date, shiftId: S.shiftId, sel: S.sel };
      S.date = n === 0 ? todayIn(S.tz) : addDaysISO(S.date, n);
      // Keep the chosen shift: the server falls back to a sensible one when it does not run that day.
      S.sel = null;
      try {
        await load();
      } catch (e) {
        // Keep showing the day that works rather than a half-changed screen.
        Object.assign(S, prev);
        toast(T('h.toast.load_day_failed', { date: S.date, error: errMsg(e) }), true);
      }
      return;
    }
    case 'plan':
      S.planId = id;
      render();
      return;
    case 'shift': {
      const prev = S.shiftId;
      S.shiftId = id;
      try {
        await load();
      } catch (e) {
        S.shiftId = prev;
        toast(T('h.toast.load_shift_failed', { error: errMsg(e) }), true);
      }
      return;
    }
    case 'view':
      S.view = el.dataset.v as View;
      render();
      if (S.view === 'calendar') await loadCalendar();
      return;
    case 'cal-nav': {
      const [y, m] = S.calMonth.split('-').map(Number);
      const d = new Date(Date.UTC(y, m - 1 + Number(el.dataset.n), 1));
      S.calMonth = d.toISOString().slice(0, 7);
      await loadCalendar();
      return;
    }
    case 'cal-day':
      S.date = el.dataset.day!;
      S.shiftId = '';
      S.view = 'floor';
      await load();
      return;
    case 'feed':
      await feedModal();
      return;
    case 'problems':
      problemsModal();
      return;
    case 'audit':
      await auditModal();
      return;
    case 'as-of':
      asOfModal();
      return;
    case 'status-pick':
      statusPicker(id);
      return;
    case 'confirm-table':
      return;
    case 'toggle':
      S.collapsed.has(id) ? S.collapsed.delete(id) : S.collapsed.add(id);
      render();
      return;
    case 'sel-party':
      S.sel = { kind: 'party', id };
      S.mode = null;
      render();
      return;
    case 'sel-wait':
      S.sel = { kind: 'wait', id };
      render();
      return;
    case 'sel-table':
      await onTableClick(id);
      return;
    case 'close-drawer':
      S.sel = null;
      render();
      return;
    case 'cancel-mode':
      S.mode = null;
      render();
      return;
    case 'book':
      bookModal();
      return;
    case 'waitlist-add':
      waitlistModal();
      return;
    case 'statuses':
      statusesModal();
      return;
    case 'seat': {
      const p = party(id);
      if (p.tableIds.length) await act(() => api('POST', `/v1/host/reservations/${id}/seat`, {}), T('h.toast.seated'));
      else {
        S.mode = { kind: 'seat', partyId: id };
        S.view = 'floor';
        render();
      }
      return;
    }
    case 'assign':
      S.mode = { kind: 'assign', partyId: id };
      S.view = 'floor';
      render();
      return;
    case 'move':
      S.mode = { kind: 'move', partyId: id };
      S.view = 'floor';
      render();
      return;
    case 'finish':
      await act(() => api('POST', `/v1/host/reservations/${id}/status`, { status: 'finished' }), T('h.toast.freed'));
      return;
    case 'cancel-res':
      if (window.confirm(T('h.confirm.cancel'))) {
        const r: any = await act(() => api('POST', `/v1/host/reservations/${id}/status`, { status: 'cancelled' }), T('h.toast.cancelled'));
        void r;
      }
      return;
    case 'noshow':
      if (window.confirm(T('h.confirm.noshow'))) await act(() => api('POST', `/v1/host/reservations/${id}/status`, { status: 'no_show' }), T('h.toast.noshow'));
      return;
    case 'edit':
      editModal(id);
      return;
    case 'walkin':
      walkinModal(id);
      return;
    case 'block':
      await protectedAction(
        (passcode) =>
          api('POST', `/v1/restaurants/${S.rid}/blocks`, {
            tableId: id,
            startsAt: new Date().toISOString(),
            minutes: 60,
            reason: 'Blocked by host',
            passcode,
          }),
        T('h.toast.blocked'),
      );
      return;
    case 'logout':
      try {
        await api('POST', '/v1/auth/logout');
      } catch {
        /* the cookie is cleared by the server; fall through to the login view */
      }
      loginView();
      return;
    case 'team':
      await teamModal();
      return;
    case 'invoices':
      await invoicesModal();
      return;
    case 'erase-guest': {
      if (!window.confirm(T('h.d.erase_confirm'))) return;
      const r: any = await act(() => api('POST', `/v1/host/reservations/${id}/erase-guest`, {}), T('h.d.erased'));
      if (r) await load();
      return;
    }
    case 'resend-email': {
      const r: any = await act(() => api('POST', `/v1/host/reservations/${id}/emails/resend`, {}), T('h.toast.queued'));
      if (r) window.setTimeout(() => void loadEmails(id), 800);
      return;
    }
    case 'config':
      openConfigScreen({
        restaurantId: S.rid,
        canPublish: can('config.publish'),
        toast,
        onPublished: () => void load(),
        onClose: () => void load(),
      });
      return;
    case 'pay-charge': {
      const due = Number(el.dataset.due ?? 0);
      if (!window.confirm(T('h.confirm.charge', { amount: money(due) }))) return;
      const r: any = await act(() => api('POST', `/v1/host/reservations/${id}/payment/charge`, {}));
      if (r) {
        if (r.ok) toast(T('h.toast.charged', { amount: money(r.amountMinor) }));
        else toast(r.pending ? T('h.toast.charge_pending') : T('h.toast.declined', { reason: r.failure ?? '' }), true);
        await load();
      }
      return;
    }
    case 'pay-refund': {
      const left = Number(el.dataset.left ?? 0);
      if (!window.confirm(T('h.confirm.refund', { amount: money(left) }))) return;
      const r: any = await act(() => api('POST', `/v1/host/reservations/${id}/payment/refund`, {}));
      if (r) {
        toast(r.ok ? T('h.toast.refunded', { amount: money(r.amountMinor) }) : T('h.toast.refund_pending'), !r.ok);
        await load();
      }
      return;
    }
    case 'password':
      await securityModal(false);
      return;
    case 'forgot':
      loginView('', 'forgot');
      return;
    case 'back-login':
      pendingLogin = null;
      loginView();
      return;
    case 'wait-notify': {
      const r: any = await act(() => api('POST', `/v1/waitlist/${id}/notify`, {}));
      if (r) toast(r.sms && r.smsSent ? T('h.toast.notified_sms', { to: r.sms.to }) : T('h.toast.notified'));
      return;
    }
    case 'wait-seat':
      S.mode = { kind: 'waitseat', waitId: id };
      S.view = 'floor';
      render();
      return;
    case 'wait-remove':
      await act(() => api('DELETE', `/v1/waitlist/${id}`), T('h.toast.removed'));
      S.sel = null;
      return;
    case 'add-note': {
      const body = (document.getElementById('note-body') as HTMLInputElement).value.trim();
      const scope = (document.getElementById('note-scope') as HTMLSelectElement).value;
      if (!body) return;
      try {
        await api('POST', `/v1/host/reservations/${id}/notes`, { scope, body });
        (document.getElementById('note-body') as HTMLInputElement).value = '';
        await loadNotes(id);
      } catch (e) {
        toast(errMsg(e), true);
      }
      return;
    }
    case 'tl-click': {
      if ((ev.target as HTMLElement).closest('.tl-bar')) return;
      const track = el;
      const rect = track.getBoundingClientRect();
      const t0 = Number(track.dataset.t0);
      const px = Number(track.dataset.px);
      const minutes = Math.round((t0 + (ev.clientX - rect.left) / px) / 15) * 15;
      const tbl = table(track.dataset.table!);
      if (!tbl) return;
      if (!can('reservations.manage')) {
        S.sel = { kind: 'table', id: tbl.id };
        S.view = 'floor';
        render();
        return;
      }
      tableSlotModal(tbl, minutes);
      return;
    }
    case 'quote':
      waitlistModal({ party: Number(el.dataset.n), minutes: Number(el.dataset.min) });
      return;
    case 'search-pick': {
      const date = el.dataset.date!;
      S.search = '';
      if (date !== S.date) {
        S.date = date;
        S.shiftId = '';
      }
      await load();
      S.sel = { kind: 'party', id };
      S.view = S.view === 'timeline' ? 'timeline' : 'floor';
      render();
      return;
    }
    case 'quick-status':
      await act(() => api('POST', `/v1/host/reservations/${id}/status`, { status: el.dataset.s }), T('h.toast.updated'));
      return;
    case 'sms': {
      const kind = el.dataset.kind!;
      let text: string | undefined;
      if (kind === 'custom') {
        const typed = window.prompt(T('h.x.sms_prompt'), '');
        if (!typed?.trim()) return;
        text = typed.trim();
      }
      try {
        const r = await api<any>('POST', `/v1/host/reservations/${id}/sms`, { kind, text });
        toast(T('h.x.sms_sent', { n: r.left }));
        await loadHistory(id);
      } catch (e) {
        toast(errMsg(e), true);
      }
      return;
    }
    case 'save-guestbook': {
      const gid = el.dataset.gid!;
      const box = document.getElementById('guestbook')!;
      const tags = [...box.querySelectorAll<HTMLInputElement>('[data-tag]')].filter((c) => c.checked).map((c) => c.dataset.tag!);
      const notes: Record<string, string> = {};
      box.querySelectorAll<HTMLInputElement>('[data-gnote]').forEach((i) => (notes[i.dataset.gnote!] = i.value.trim()));
      try {
        await api('PATCH', `/v1/restaurants/${S.rid}/guests/${gid}`, { tags, profileNotes: notes });
        toast(T('h.toast.updated'));
        await load();
      } catch (e) {
        toast(errMsg(e), true);
      }
      return;
    }
    case 'shift-notes':
      shiftNotesModal();
      return;
    case 'guests':
      openGuestsScreen({ restaurantId: S.rid, toast, canExport: can('guests.manage'), onClose: () => void load() });
      return;
    case 'extras':
      openExtrasScreen({
        restaurantId: S.rid,
        currency: D()?.restaurant?.currency ?? 'EUR',
        can: (p) => can(p as any),
        toast,
        onClose: () => void load(),
      });
      return;
    case 'reports':
      openReportsScreen({ restaurantId: S.rid, toast, timezone: S.tz, onClose: () => undefined });
      return;
    default:
  }
}

document.addEventListener('click', (ev) => {
  onDocumentClick(ev).catch((e) => {
    console.error(e);
    toast(errMsg(e), true);
  });
});

let searchTimer = 0 as any;

async function runSearch(term: string) {
  const box = document.getElementById('search-results');
  if (!box) return;
  if (term.trim().length < 2) {
    box.innerHTML = '';
    return;
  }
  try {
    const r = await api<any>('GET', `/v1/restaurants/${S.rid}/search?q=${encodeURIComponent(term.trim())}`);
    // The box may have been redrawn while the request was in flight.
    const now = document.getElementById('search-results');
    if (!now) return;
    now.innerHTML = r.results.length
      ? r.results
          .map(
            (x: any) => `<button data-act="search-pick" data-id="${x.id}" data-date="${esc(x.date)}">
              <strong>${esc(x.name || x.code)}</strong> <span class="small">${x.partySize} · ${esc(fmtDate(x.date))} ${esc(tm(x.startsAt))}</span>
              <span class="small muted">${esc(statusLabel(x.status))}${x.phone ? ' · ' + esc(x.phone) : ''}</span></button>`,
          )
          .join('')
      : `<span class="muted small">${esc(T('h.x.no_results'))}</span>`;
  } catch {
    box.innerHTML = '';
  }
}

document.addEventListener('input', (ev) => {
  const el = ev.target as HTMLInputElement;
  if (el.id !== 'host-search') return;
  S.search = el.value;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => void runSearch(el.value), 250);
});

document.addEventListener('change', async (ev) => {
  const el = ev.target as HTMLElement;
  if (el.hasAttribute('data-switch')) {
    const rid = (el as HTMLSelectElement).value;
    if (!rid) return;
    try {
      await api('POST', '/v1/auth/switch', { restaurantId: rid });
      location.href = `/host?rid=${rid}`;
    } catch (e) {
      toast(errMsg(e), true);
    }
    return;
  }
  if (el.id === 'referral') {
    const input = el as HTMLInputElement;
    await act(() => api('PATCH', `/v1/host/reservations/${input.dataset.id}`, { referral: input.value.trim() || null }), T('h.toast.updated'));
    return;
  }
  if (el.hasAttribute('data-lang-select')) {
    const l = (el as HTMLSelectElement).value;
    if (LOCALES.includes(l as (typeof LOCALES)[number])) {
      setLocale(l as (typeof LOCALES)[number]);
      if (S.user && S.data) render();
      else loginView();
    }
    return;
  }
  const a = el.dataset.act;
  if (!a) return;
  const id = el.dataset.id!;
  if (a === 'set-status') {
    const v = (el as HTMLSelectElement).value;
    await act(() => api('POST', `/v1/host/reservations/${id}/status`, { status: v }), T('h.toast.status'));
  } else if (a === 'exclude') {
    await act(() => api('PATCH', `/v1/host/reservations/${id}`, { excludeFromPacing: (el as HTMLInputElement).checked }), T('h.toast.updated'));
  }
});

document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Escape') {
    if (S.modalOpen) closeModal();
    else if (S.mode) {
      S.mode = null;
      render();
    }
  }
  if ((ev.key === 'Enter' || ev.key === ' ') && (ev.target as HTMLElement).matches?.('[role="button"][data-act]')) {
    ev.preventDefault();
    (ev.target as HTMLElement).click();
  }
});

init().catch((e) => {
  app.innerHTML = `<p class="loading">${esc((e as Error).message)}</p>`;
});
